import { PlaceholderFillerModal } from "@/components/modals/PlaceholderFillerModal";
import { EditVariableModal } from "@/components/modals/EditVariableModal";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// TipTap v3
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import TiptapImage from "@tiptap/extension-image";

// Yjs collaboration (optional — falls back to local-only if WS fails)
import { Collaboration } from "@tiptap/extension-collaboration";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

// ─── Custom Redline Extensions ─────────────────────────────────────────────
const RedlineIns = Mark.create({
    name: 'redlineIns',
    parseHTML() { return [{ tag: 'ins' }, { tag: 'span', getAttrs: (node: any) => node.classList.contains('redline-ins') && null }] },
    renderHTML({ HTMLAttributes }) { return ['ins', mergeAttributes(HTMLAttributes, { class: 'redline-ins' }), 0] },
});

const Highlight = Mark.create({
    name: 'highlight',
    parseHTML() { return [{ tag: 'mark' }] },
    renderHTML({ HTMLAttributes }) { return ['mark', mergeAttributes(HTMLAttributes, { class: 'citation-highlight' }), 0] },
});

const RedlineDel = Mark.create({
    name: 'redlineDel',
    parseHTML() { return [{ tag: 'del' }, { tag: 'span', getAttrs: (node: any) => node.classList.contains('redline-del') && null }] },
    renderHTML({ HTMLAttributes }) { return ['del', mergeAttributes(HTMLAttributes, { class: 'redline-del' }), 0] },
});

const CommentMark = Mark.create({
    name: 'comment',
    addAttributes() {
        return {
            commentId: {
                default: null,
                parseHTML: element => element.getAttribute('data-comment-id'),
                renderHTML: attributes => ({
                    'data-comment-id': attributes.commentId,
                    'class': 'comment-highlight',
                }),
            },
        }
    },
    parseHTML() { return [{ tag: 'span[data-comment-id]' }] },
    renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0] },
});




// ─── ProseMirror Dynamic Variable Chip Decoration Extension ───────────────
const VariableChipExtension = Extension.create({
    name: 'variableChipDecoration',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('variableChips'),
                props: {
                    decorations(state) {
                        const decorations: Decoration[] = [];
                        const regex = /\[(?![^\]]*\]\()([^\[\]\r\n]{2,120})\]/g;
                        state.doc.descendants((node, pos) => {
                            if (node.isText && node.text) {
                                let match: RegExpExecArray | null;
                                while ((match = regex.exec(node.text)) !== null) {
                                    const rawKey = match[1].trim();
                                    if (
                                        rawKey.startsWith('DOCUMENTO') ||
                                        rawKey.startsWith('CONTEXTO') ||
                                        rawKey.startsWith('FIN') ||
                                        rawKey.startsWith('ADVERTENCIA') ||
                                        rawKey.startsWith('FUENTE') ||
                                        rawKey.startsWith('http')
                                    ) {
                                        continue;
                                    }
                                    const from = pos + match.index;
                                    const to = from + match[0].length;
                                    decorations.push(
                                        Decoration.inline(from, to, {
                                            class: 'variable-chip-mark',
                                            'data-variable': rawKey,
                                            title: `Click para editar variable [${rawKey}]`,
                                        })
                                    );
                                }
                            }
                        });
                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },
});

const ResizableImage = TiptapImage.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: '200px',
                parseHTML: element => element.getAttribute('width') || element.style.width || '200px',
                renderHTML: attributes => {
                    return {
                        width: attributes.width,
                        style: `width: ${attributes.width}; max-width: 100%; height: auto;`,
                    };
                },
            },
        };
    },
});

// Sprint 3 — Digital Signatures
import SignaturePanel from "@/components/signatures/SignaturePanel";
import { jsPDF } from "jspdf";
import { asBlob } from "html-docx-js-typescript";
import { saveAs } from "file-saver";
import { marked } from "marked";
import { FileDown, Download, ChevronDown, FileText, Loader2, ShieldCheck, Share2, Briefcase, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useApiClient } from "@/hooks/use-api-client";
import { useIsMobile } from "@/hooks/use-mobile";
import LegalDisclaimer from "../LegalDisclaimer";
import * as scraperApi from "@/services/scraperApi";


const WS_BASE = (() => {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
    return backendUrl.replace(/^https?:/, backendUrl.startsWith("https") ? "wss:" : "ws:");
})();
const API = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

// ─── Types ─────────────────────────────────────────────────────────────────

interface ActiveUser { user_id: string; user_name: string; color: string; }
interface Comment {
    id?: number; user_name: string; anchor_text?: string;
    body: string; resolved: boolean; created_at: string;
}
interface Approval {
    approver_name: string; status: "pending" | "approved" | "rejected"; comment?: string;
}
interface LegalEditorProps {
    documentId: string;
    documentTitle?: string;
    initialContent?: string;
    syncToken?: number;
    userId?: string;
    userName?: string;
    templatePath?: string;
    onSave?: (content: string) => void;
    onUpdate?: (content: string) => void;
    onInsertSignature?: (base64: string) => void;
    isPublicShare?: boolean;
    shareToken?: string;
    accessLevel?: "view" | "comment" | "edit";
}

// ─── Toolbar ───────────────────────────────────────────────────────────────

const Toolbar: React.FC<{ editor: ReturnType<typeof useEditor>; isLegalFormat?: boolean; toggleLegalFormat?: () => void }> = ({ editor, isLegalFormat, toggleLegalFormat }) => {
    if (!editor) return null;
    const btn = (active: boolean, label: string, onClick: () => void, key?: string) => (
        <button key={key || label} onClick={onClick}
            className={`toolbar-btn ${active ? "active" : ""}`} title={label} type="button">
            {label}
        </button>
    );
    return (
        <div className="editor-toolbar">
            {btn(editor.isActive("bold"), "B", () => editor.chain().focus().toggleBold().run())}
            {btn(editor.isActive("italic"), "I", () => editor.chain().focus().toggleItalic().run())}
            {btn(editor.isActive("heading", { level: 1 }), "H1", () => editor.chain().focus().toggleHeading({ level: 1 }).run(), "H1")}
            {btn(editor.isActive("heading", { level: 2 }), "H2", () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "H2")}
            {btn(editor.isActive("bulletList"), "• Lista", () => editor.chain().focus().toggleBulletList().run(), "ul")}
            {btn(editor.isActive("orderedList"), "1. Lista", () => editor.chain().focus().toggleOrderedList().run(), "ol")}
            {btn(editor.isActive("blockquote"), "❝ Cita", () => editor.chain().focus().toggleBlockquote().run(), "bq")}
            <span className="toolbar-separator" />
            {toggleLegalFormat && (
                <button
                    key="beautify"
                    onClick={toggleLegalFormat}
                    className={`toolbar-btn ${isLegalFormat ? "active" : ""}`}
                    title="Activar/Desactivar Formato Legal"
                    type="button"
                    style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                    ✨ Formato Legal
                    <span style={{
                        background: '#ea4335', color: '#fff', fontSize: '0.55rem',
                        padding: '2px 5px', borderRadius: '99px', fontWeight: 800,
                        letterSpacing: '0.02em', textTransform: 'uppercase',
                        lineHeight: 1
                    }}>
                        Nuevo
                    </span>
                </button>
            )}
            <span className="toolbar-separator" />
            {btn(false, "↩ Deshacer", () => editor.chain().focus().undo().run(), "undo")}
            {btn(false, "↪ Rehacer", () => editor.chain().focus().redo().run(), "redo")}
            <span className="toolbar-separator" />
            {btn(false, "📄 + Página", () => {
                editor.chain().focus().insertContent('<div class="page-break" style="page-break-after: always; break-after: page; margin: 3rem 0; border-top: 2px dashed #94a3b8; position: relative; text-align: center;"><span style="position: relative; top: -0.75rem; background: #ffffff; padding: 2px 12px; color: #64748b; font-size: 11px; font-weight: 700; border: 1px solid #cbd5e1; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.05em; user-select: none;">📄 Salto de Página / Nueva Página</span></div><p></p>').run();
                toast.success("Nueva página añadida al documento");
            }, "page-break")}
            <span className="toolbar-separator" />
            {btn(false, "🔄 Refrescar", () => {
                const html = editor.getHTML();
                editor.commands.setContent(html);
                toast.info("Actualizando visualización de redlines...");
            }, "refresh")}
        </div>
    );
};

// ─── Main Editor ────────────────────────────────────────────────────────────

// Helper functions for learning and auto-filling template placeholders
const extractPlaceholderContexts = (html: string) => {
  const placeholderRegex = /\[([A-Z0-9_áéíóúñ\s]+)\]/gi;
  let match;
  const contexts: { placeholder: string; prefix: string; suffix: string }[] = [];
  while ((match = placeholderRegex.exec(html)) !== null) {
    const placeholder = match[0];
    const key = match[1].toUpperCase().trim();
    const index = match.index;
    const prefixStart = Math.max(0, index - 25);
    const prefix = html.substring(prefixStart, index);
    const suffixEnd = Math.min(html.length, index + placeholder.length + 25);
    const suffix = html.substring(index + placeholder.length, suffixEnd);
    contexts.push({ placeholder: key, prefix, suffix });
  }
  return contexts;
};

const processAutoFill = (content: string) => {
  const placeholderRegex = /\[([A-Z0-9_áéíóúñ\s]+)\]/gi;
  const matches = content.match(placeholderRegex) || [];
  if (matches.length === 0) return { content, autoFilled: [] };

  let updatedContent = content;
  const filledFields: string[] = [];

  matches.forEach((placeholder) => {
    const cleanedKey = placeholder.slice(1, -1).toUpperCase().trim();
    const learnedValue = localStorage.getItem(`learned_placeholder_${cleanedKey}`);
    if (learnedValue && learnedValue !== "null" && learnedValue !== "undefined") {
      const escapedPlaceholder = placeholder.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      updatedContent = updatedContent.replace(new RegExp(escapedPlaceholder, 'g'), `<strong>${learnedValue}</strong>`);
      if (!filledFields.includes(cleanedKey)) {
        filledFields.push(cleanedKey);
      }
    }
  });

  return { content: updatedContent, autoFilled: filledFields };
};

const learnEditedValues = (currentHtml: string, contexts: { placeholder: string; prefix: string; suffix: string }[]) => {
  if (!contexts || contexts.length === 0) return;
  contexts.forEach(({ placeholder, prefix, suffix }) => {
    const escapeRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    const regex = new RegExp(escapeRegExp(prefix) + "(.*?)" + escapeRegExp(suffix), "i");
    const match = currentHtml.match(regex);
    if (match && match[1]) {
      const learnedValue = match[1].replace(/<[^>]*>/g, "").trim();
      if (
        learnedValue && 
        !learnedValue.includes("[") && 
        !learnedValue.includes("]") && 
        learnedValue.length < 120 &&
        learnedValue !== "null" &&
        learnedValue !== "undefined" &&
        learnedValue !== "—"
      ) {
        const storageKey = `learned_placeholder_${placeholder}`;
        const existing = localStorage.getItem(storageKey);
        if (existing !== learnedValue) {
          localStorage.setItem(storageKey, learnedValue);
        }
      }
    }
  });
};

const cleanResidualChips = (content: string): string => {
    if (!content) return content;
    return content.replace(/<span[^>]*class="[^"]*variable-chip-mark[^"]*"[^>]*data-variable="([^"]*)"[^>]*>[\s\S]*?<\/span>/gi, '[$1]');
};


const LegalEditor: React.FC<LegalEditorProps> = ({
    documentId, documentTitle, initialContent = "", syncToken = 0, userId = "anonymous", userName = "Abogado", templatePath = "", onSave, onUpdate,
    isPublicShare = false,
    shareToken = "",
    accessLevel = "edit",
}) => {
    const navigate = useNavigate();
    const apiClient = useApiClient();

    const [currentTitle, setCurrentTitle] = useState<string>(documentTitle || "Documento jurídico");
    const [renaming, setRenaming] = useState(false);

    useEffect(() => {
        if (documentTitle) {
            setCurrentTitle(documentTitle);
        }
    }, [documentTitle]);

    const handleHeaderRename = async () => {
        const newName = window.prompt("Ingresa el nuevo nombre para este documento:", currentTitle);
        if (!newName || !newName.trim() || newName.trim() === currentTitle) return;

        const trimmed = newName.trim();
        const previousTitle = currentTitle;

        // ⚡ Optimistic Update: Update title IMMEDIATELY in header (0ms delay)
        setCurrentTitle(trimmed);
        try {
            sessionStorage.removeItem("cached_my_documents");
        } catch {}

        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/rename`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ name: trimmed })
            });

            if (res.ok) {
                toast.success("Nombre del documento actualizado");
            } else {
                setCurrentTitle(previousTitle);
                const errData = await res.json().catch(() => ({}));
                toast.error(`Error: ${errData.detail || "No se pudo cambiar el nombre"}`);
            }
        } catch (err) {
            setCurrentTitle(previousTitle);
            console.error(err);
            toast.error("Error al conectar con el servidor.");
        }
    };

    // Y.Doc is created synchronously so it's available for Collaboration on first render
    const ydocRef = useRef(new Y.Doc());
    const provRef = useRef<WebsocketProvider | null>(null);

    const isMobile = useIsMobile();
    const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
    const [isLegalFormat, setIsLegalFormat] = useState(Boolean(templatePath && templatePath !== "none"));
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);

    // ── Share Modal State (Google Docs Link + Team Collaborators) ───────────
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareModalTab, setShareModalTab] = useState<"client" | "team">("client");
    const [clientPerm, setClientPerm] = useState<"edit" | "comment" | "view">("edit");
    const [clientNameInput, setClientNameInput] = useState("");
    const [generatedLink, setGeneratedLink] = useState("");
    const [linkGenerating, setLinkGenerating] = useState(false);
    const [activeShares, setActiveShares] = useState<any[]>([]);
    const [teamUserId, setTeamUserId] = useState("");
    const [teamRole, setTeamRole] = useState("editor");
    const [teamCollaborators, setTeamCollaborators] = useState<any[]>([]);
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamSearchQuery, setTeamSearchQuery] = useState("");
    const [teamSuggestions, setTeamSuggestions] = useState<any[]>([]);
    const [isSearchingTeam, setIsSearchingTeam] = useState(false);
    const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false);
    const downloadRef = useRef<HTMLDivElement>(null);
    const shareRef = useRef<HTMLDivElement>(null);
    const mobileMoreMenuRef = useRef<HTMLDivElement>(null);

    // ─── Click Outside Dropdowns ─────────────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (downloadRef.current && !downloadRef.current.contains(event.target as Node)) {
                setShowDownloadMenu(false);
            }
            if (shareRef.current && !shareRef.current.contains(event.target as Node)) {
                setShowShareMenu(false);
            }
            if (mobileMoreMenuRef.current && !mobileMoreMenuRef.current.contains(event.target as Node)) {
                setShowMobileMoreMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const [connected, setConnected] = useState(false);
    const [collabAvailable, setCollabAvailable] = useState(true);

    // Process linking states
    const [showLinkProcesoModal, setShowLinkProcesoModal] = useState(false);
    const [procesos, setProcesos] = useState<any[]>([]);
    const [loadingProcesos, setLoadingProcesos] = useState(false);
    const [selectedProcesoRadicado, setSelectedProcesoRadicado] = useState("");
    const [linkProcesoFormat, setLinkProcesoFormat] = useState<"docx" | "pdf">("docx");
    const [linkProcesoFileName, setLinkProcesoFileName] = useState("");
    const [isLinkingProceso, setIsLinkingProceso] = useState(false);
    const [comments, setComments] = useState<Comment[]>([]);
    const [approvals, setApprovals] = useState<Approval[]>([]);
    const [showComments, setShowComments] = useState(false);
    const [showApprovals, setShowApprovals] = useState(false);
    const [showSignatures, setShowSignatures] = useState(false);
    const [showAiMode, setShowAiMode] = useState(false);
    const [aiInstructions, setAiInstructions] = useState("");
    const [isRefining, setIsRefining] = useState(false);
    const [letterheadTemplates, setLetterheadTemplates] = useState<any[]>([]);
    const [loadingLetterheads, setLoadingLetterheads] = useState(false);
    const [showLetterheadModal, setShowLetterheadModal] = useState(false);
    const [renderingLetterhead, setRenderingLetterhead] = useState(false);
    const placeholderContextsRef = useRef<{ placeholder: string; prefix: string; suffix: string }[]>([]);
    const [newComment, setNewComment] = useState("");
    const [commentLoading, setCommentLoading] = useState(false);
    const [charCount, setCharCount] = useState(0);
    const [contentInjected, setContentInjected] = useState(false);
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [analyzing, setAnalyzing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [activeCommentId, setActiveCommentId] = useState<number | null>(null);
    const [isFillerModalOpen, setIsFillerModalOpen] = useState(false);
    const [editingVariable, setEditingVariable] = useState<string | null>(null);
    const [placeholders, setPlaceholders] = useState<string[]>([]);
    const hasAutoOpenedModalRef = useRef(false);

    // ── Email Share Modal ────────────────────────────────────────────────────
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailModalFormat, setEmailModalFormat] = useState<'pdf' | 'docx'>('pdf');
    const [shareEmail, setShareEmail] = useState("");
    const [shareName, setShareName] = useState("");
    const [shareMessage, setShareMessage] = useState("");
    const [shareSending, setShareSending] = useState(false);

    // ── WebSocket provider (connects after mount, tolerates failure) ──────────
    // ── WebSocket provider Memo fallback ─────────────────────────────────────
    const wsServerUrl = useMemo(() => {
        return `${WS_BASE}/api/v1/collab/documents`;
    }, []);

    useEffect(() => {
        let provider: WebsocketProvider | null = null;
        try {
            const wsParams = (isPublicShare && shareToken)
                ? { share_token: shareToken, user_name: userName || "Cliente" }
                : { user_id: userId, user_name: userName };

            provider = new WebsocketProvider(wsServerUrl, documentId, ydocRef.current, {
                connect: true,
                maxBackoffTime: 5000,
                params: wsParams,
            });
            provRef.current = provider;

            provider.awareness.setLocalStateField("user", { name: userName, color: "#1a73e8" });

            provider.on("status", ({ status }: { status: string }) => {
                const isConnected = status === "connected";
                setConnected(isConnected);
                if (isConnected) setCollabAvailable(true);
            });

            provider.awareness.on("change", () => {
                const users: ActiveUser[] = Array.from(provider!.awareness.getStates().entries())
                    .filter(([id]) => id !== provider!.awareness.clientID)
                    .map(([id, s]: [number, any]) => ({
                        user_id: String(id),
                        user_name: s.user?.name || "Anónimo",
                        color: s.user?.color || "#1a73e8",
                    }));
                setActiveUsers(users);
            });

            // If WebSocket fails to connect after 10s, mark collab as unavailable
            const timeout = setTimeout(() => {
                if (provider && !provider.wsconnected) {
                    setCollabAvailable(false);
                    toast.warning("Modo colaboración no disponible. Trabajando en modo local.");
                }
            }, 10000);

            fetchComments();
            fetchApprovals();

            return () => {
                clearTimeout(timeout);
                provider?.destroy();
                provRef.current = null;
            };
        } catch (err) {
            console.warn("[LegalEditor] WebSocket provider init failed, working offline:", err);
            setCollabAvailable(false);
            fetchComments();
            fetchApprovals();
        }
    }, [wsServerUrl, documentId, userId, userName]); // Re-run only if URL, ID, or user details change

    // ── TipTap editor ─────────────────────────────────────────────────────────
    const editor = useEditor({
        editable: isPublicShare ? (accessLevel === "edit" || (accessLevel as any) === "full_edit") : true,
        extensions: [
            StarterKit.configure({ undoRedo: false }),
            Placeholder.configure({ placeholder: "Comienza a redactar el documento jurídico…" }),
            CharacterCount,
            Collaboration.configure({ document: ydocRef.current }),
            RedlineIns,
            RedlineDel,
            Highlight,
            CommentMark,
            VariableChipExtension,
            ResizableImage.configure({
                allowBase64: true,
                HTMLAttributes: {
                    class: 'signature-image',
                },
            }),
        ],
        // NO redundant marked.parse in the initial content prop to avoid escaping tags
        // Ensure we parse Markdown if markers are present, even if HTML is also there
        content: initialContent ? (
            initialContent.trim().startsWith("<")
                ? cleanResidualChips(initialContent)
                : cleanResidualChips(/\*\*|###|__|\* |[0-9]+\. /m.test(initialContent) ? marked.parse(initialContent) as string : initialContent)
        ) : "",
        onUpdate: ({ editor: e }) => {
            setCharCount(e.storage.characterCount?.characters?.() ?? 0);
            if (onUpdate) {
                onUpdate(e.getHTML());
            }
        },
        onSelectionUpdate: ({ editor: e }) => {
            const attrs = e.getAttributes('comment');
            if (attrs.commentId) {
                setActiveCommentId(Number(attrs.commentId));
                // Scroll the sidebar if needed
                const commentEl = document.getElementById(`comment-${attrs.commentId}`);
                if (commentEl) {
                    commentEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            } else {
                setActiveCommentId(null);
            }
        },
        editorProps: {
            handleClick: (view, pos, event) => {
                const target = event.target as HTMLElement;
                const chip = target.closest('.variable-chip-mark') as HTMLElement | null;
                if (chip) {
                    event.preventDefault();
                    event.stopPropagation();
                    const varName = chip.getAttribute('data-variable') || chip.innerText.replace(/^\[|\]$/g, '').trim();
                    handleEditSingleVariable(varName);
                    return true;
                }
                return false;
            },
            handleDrop: (view, event, slice, moved) => {
                if (!moved && event.dataTransfer) {
                    const base64 = event.dataTransfer.getData("signature-base64");
                    if (base64) {
                        const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                        if (coordinates) {
                            const { schema } = view.state;
                            const node = schema.nodes.image.create({
                                src: base64,
                                class: 'signature-image',
                                width: '200px'
                            });
                            const transaction = view.state.tr.insert(coordinates.pos, node);
                            view.dispatch(transaction);
                            return true;
                        }
                    }
                }
                return false;
            }
        },
    });

    const lastInjectedRef = useRef<string>("");
    const lastSyncTokenRef = useRef<number>(0);

    // ── Inject initial content if empty or if fresh redlines arrived ──────────
    useEffect(() => {
        if (!editor || !initialContent) return;

        const isManualSync = syncToken > 0 && lastSyncTokenRef.current !== syncToken;

        if (!contentInjected || isManualSync) {
            const isHtml = initialContent.trim().startsWith("<");
            const hasMarkdown = !isHtml && /\*\*|###|__|^[*-] |^[0-9]+\. /m.test(initialContent);
            const finalContent = hasMarkdown ? marked.parse(initialContent) : initialContent;

            // Extract template contexts for learning replacements later
            placeholderContextsRef.current = extractPlaceholderContexts(finalContent as string);

            // Auto-fill placeholders with learned data
            const { content: autoFilledContent, autoFilled } = processAutoFill(finalContent as string);
            const cleanContent = cleanResidualChips(autoFilledContent as string);

            // Preserve selection if possible
            const { from, to } = editor.state.selection;

            editor.commands.setContent(cleanContent, { emitUpdate: true });

            if (autoFilled.length > 0) {
                toast.success(`Datos rellenados automáticamente: ${autoFilled.join(", ")}`);
            }

            // Restore selection if it was within bounds
            try {
                const newSize = editor.state.doc.content.size;
                if (from < newSize && to < newSize) {
                    editor.commands.setTextSelection({ from, to });
                }
            } catch (e) {
                // Ignore selection errors
            }

            lastInjectedRef.current = initialContent;
            lastSyncTokenRef.current = syncToken;
            setContentInjected(true);
        }
    }, [editor, initialContent, contentInjected, syncToken]);

    // ── Citation/Text Highlighting ──────────────────────────────────────────
    useEffect(() => {
        const handleHighlight = (e: any) => {
            const { text } = e.detail;
            if (!editor || !text) return;


            const { state } = editor;
            let foundPos = -1;
            let matchLength = text.length;

            // Step 1: Exact or case-insensitive match
            state.doc.descendants((node, pos) => {
                if (node.isText && node.text) {
                    const idx = node.text.toLowerCase().indexOf(text.toLowerCase());
                    if (idx !== -1) {
                        foundPos = pos + idx;
                        return false; // stop iteration
                    }
                }
                return true;
            });

            // Step 2: Try chunk-based search (ignoring punctuation differences from LLM)
            if (foundPos === -1 && text.length > 20) {
                const chunks = text.split(/[.,;\n:]/).filter((c: string) => c.trim().length > 15);
                for (const chunk of chunks) {
                    const cleanChunk = chunk.trim().toLowerCase();
                    state.doc.descendants((node, pos) => {
                        if (node.isText && node.text) {
                            const idx = node.text.toLowerCase().indexOf(cleanChunk);
                            if (idx !== -1) {
                                foundPos = pos + idx;
                                matchLength = cleanChunk.length;
                                return false;
                            }
                        }
                        return true;
                    });
                    if (foundPos !== -1) break;
                }
            }

            if (foundPos !== -1) {
                // Remove previous highlights
                editor.commands.unsetMark('highlight');

                // Select and apply highlight mark
                editor.commands.setTextSelection({ from: foundPos, to: foundPos + matchLength });
                editor.commands.setMark('highlight');
                editor.commands.focus();
                editor.commands.scrollIntoView();

                toast.success("Ubicado en el contrato");

                // Auto-remove highlight after animation
                setTimeout(() => {
                    if (editor) {
                        editor.commands.unsetMark('highlight');
                    }
                }, 3000);
            } else {
                // Step 3: Native Browser Fallback
                // Try finding the first 40 characters using the browser's native text search
                const searchSnippet = text.trim().substring(0, 40);
                // Reset selection to start of document so window.find searches forward
                window.getSelection()?.removeAllRanges();
                if (searchSnippet && (window as any).find(searchSnippet, false, false, true, false, true, false)) {
                    toast.success("Ubicado en el contrato");
                } else {
                    toast.error("No se pudo localizar el texto exacto en el editor. Es posible que el texto original haya sido modificado o esté dividido en múltiples bloques.");
                    console.warn("[LegalEditor] Text not found for highlighting:", text);
                }
            }
        };

        window.addEventListener("editor-highlight", handleHighlight);
        return () => window.removeEventListener("editor-highlight", handleHighlight);
    }, [editor]);

    // ── Drag Resizing for Signature Images ────────────────────────────────────
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;

        let cleanup: (() => void) | undefined;

        const attachListeners = (domElement: HTMLElement) => {
            let isResizing = false;
            let startX = 0;
            let startWidth = 0;
            let activeImage: HTMLImageElement | null = null;

            const handleMouseMove = (e: MouseEvent) => {
                if (isResizing) return;
                const target = e.target as HTMLElement;
                if (target && target.tagName === 'IMG' && target.classList.contains('signature-image')) {
                    const rect = target.getBoundingClientRect();
                    const mouseX = e.clientX - rect.left;
                    const mouseY = e.clientY - rect.top;
                    if (rect.width - mouseX < 15 && rect.height - mouseY < 15) {
                        target.style.cursor = 'se-resize';
                    } else {
                        target.style.cursor = 'pointer';
                    }
                }
            };

            const handleMouseDown = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (target && target.tagName === 'IMG' && target.classList.contains('signature-image')) {
                    const rect = target.getBoundingClientRect();
                    const mouseX = e.clientX - rect.left;
                    const mouseY = e.clientY - rect.top;
                    if (rect.width - mouseX < 15 && rect.height - mouseY < 15) {
                        e.preventDefault();
                        e.stopPropagation();
                        isResizing = true;
                        startX = e.clientX;
                        startWidth = rect.width;
                        activeImage = target as HTMLImageElement;
                        document.addEventListener('mousemove', handleGlobalMouseMove);
                        document.addEventListener('mouseup', handleGlobalMouseUp);
                    }
                }
            };

            const handleGlobalMouseMove = (e: MouseEvent) => {
                if (isResizing && activeImage) {
                    const deltaX = e.clientX - startX;
                    const newWidth = Math.max(50, Math.min(800, startWidth + deltaX));
                    activeImage.style.width = `${newWidth}px`;
                    activeImage.style.height = 'auto';
                }
            };

            const handleGlobalMouseUp = (e: MouseEvent) => {
                if (isResizing && activeImage) {
                    isResizing = false;
                    const finalWidth = activeImage.style.width || `${activeImage.getBoundingClientRect().width}px`;
                    editor.commands.focus();
                    editor.commands.updateAttributes('image', { width: finalWidth });
                    activeImage = null;
                    document.removeEventListener('mousemove', handleGlobalMouseMove);
                    document.removeEventListener('mouseup', handleGlobalMouseUp);
                }
            };

            domElement.addEventListener('mousemove', handleMouseMove);
            domElement.addEventListener('mousedown', handleMouseDown);

            return () => {
                domElement.removeEventListener('mousemove', handleMouseMove);
                domElement.removeEventListener('mousedown', handleMouseDown);
                document.removeEventListener('mousemove', handleGlobalMouseMove);
                document.removeEventListener('mouseup', handleGlobalMouseUp);
            };
        };

        try {
            if (editor.view?.dom) {
                cleanup = attachListeners(editor.view.dom);
            }
        } catch (e) {
            const timer = setTimeout(() => {
                try {
                    if (editor.view?.dom) {
                        cleanup = attachListeners(editor.view.dom);
                    }
                } catch (err) {}
            }, 100);
            return () => {
                clearTimeout(timer);
                cleanup?.();
            };
        }

        return () => {
            cleanup?.();
        };
    }, [editor]);

    // ── Comments ─────────────────────────────────────────────────────────────
    const fetchComments = async () => {
        try {
            const r = await fetch(`${API}/api/v1/documents/${documentId}/comments`);
            if (r.ok) {
                setComments(await r.json());
            } else {
                const err = await r.json().catch(() => ({ detail: "Error desconocido" }));
                console.warn("[LegalEditor] Fetch comments error:", err);
                if (r.status === 500) toast.error("Error al cargar comentarios: Error de base de datos.");
            }
        } catch (err) {
            console.error("[LegalEditor] Fetch comments exception:", err);
        }
    };
    const addComment = async () => {
        if (!newComment.trim()) return;
        setCommentLoading(true);
        const { from, to } = editor?.state.selection ?? { from: 0, to: 0 };
        const selected = editor?.state.doc.textBetween(from, to, " ") ?? "";

        try {
            const r = await fetch(`${API}/api/v1/documents/${documentId}/comments`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: userId, user_name: userName, anchor_text: selected, body: newComment }),
            });
            if (r.ok) {
                const savedComment = await r.json();

                // Apply the mark to the editor if we have a selection
                if (editor && from !== to) {
                    editor.chain()
                        .focus()
                        .setMark('comment', { commentId: savedComment.id })
                        .run();
                }

                setNewComment("");
                await fetchComments();
                toast.success("Comentario añadido");
            }
        } catch (err) {
            console.error("Error adding comment:", err);
            toast.error("No se pudo añadir el comentario");
        } finally {
            setCommentLoading(false);
        }
    };
    const resolveComment = async (id: number) => {
        await fetch(`${API}/api/v1/documents/${documentId}/comments/${id}/resolve`, { method: "POST" });
        // Optional: remove the mark from the editor when resolved?
        // editor?.commands.unsetMark('comment', { commentId: id });
        await fetchComments();
    };

    const findAndHighlightComment = (commentId: number, anchorText?: string) => {
        if (!editor) return;
        setActiveCommentId(commentId);

        const { state } = editor;
        let foundPos = -1;
        let endPos = -1;

        // Step 1: Try to find by mark
        state.doc.descendants((node, pos) => {
            const commentMark = node.marks.find(m => m.type.name === 'comment' && String(m.attrs.commentId) === String(commentId));
            if (commentMark) {
                if (foundPos === -1) foundPos = pos;
                endPos = pos + node.nodeSize;
            }
            return true;
        });

        // Step 2: Fallback to anchor_text if mark not found
        if (foundPos === -1 && anchorText) {
            state.doc.descendants((node, pos) => {
                if (node.isText && node.text) {
                    const idx = node.text.indexOf(anchorText);
                    if (idx !== -1) {
                        foundPos = pos + idx;
                        endPos = foundPos + anchorText.length;
                        // Re-apply the mark for future clicks
                        editor.chain().setTextSelection({ from: foundPos, to: endPos }).setMark('comment', { commentId }).run();
                        return false;
                    }
                }
                return true;
            });
        }

        if (foundPos !== -1) {
            editor.chain().focus().setTextSelection({ from: foundPos, to: endPos }).scrollIntoView().run();
        } else {
            toast.error("No se encontró el texto anclado");
        }
    };

    // ── Approvals ─────────────────────────────────────────────────────────────
    const fetchApprovals = async () => {
        try {
            const r = await fetch(`${API}/api/v1/documents/${documentId}/approvals`);
            if (r.ok) {
                setApprovals(await r.json());
            } else {
                if (r.status === 500) toast.error("Error al cargar aprobaciones: Error de base de datos.");
            }
        } catch { }
    };
    const submitApproval = async (status: "approved" | "rejected", comment = "") => {
        await fetch(`${API}/api/v1/documents/${documentId}/approvals`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ approver_id: userId, approver_name: userName, status, comment }),
        });
        await fetchApprovals();
    };

    const handleSave = useCallback(async () => {
        if (!editor || !onSave) return;
        setSaveStatus("saving");
        const html = editor.getHTML();
        if (html.includes("data:image")) {
        }

        try {
            learnEditedValues(html, placeholderContextsRef.current);
            await onSave(html);
            setSaveStatus("saved");
            toast.success("Documento guardado con éxito");
            setTimeout(() => setSaveStatus("idle"), 2000);
        } catch (err) {
            console.error("[LegalEditor] Save error:", err);
            setSaveStatus("error");
            toast.error("Error al guardar el documento. Revisa la consola.");
            setTimeout(() => setSaveStatus("idle"), 3000);
        }
    }, [editor, onSave, documentId, userId]);

        const scanPlaceholders = useCallback(() => {
        if (!editor) return [];
        const text = editor.getText();
        const placeholderRegex = /\[(?![^\]]*\]\()([^\[\]\r\n]{2,120})\]/g;
        const matches = text.match(placeholderRegex) || [];
        const cleaned = matches
            .map((m) => m.slice(1, -1).trim())
            .filter(
                (m) =>
                    !m.startsWith("DOCUMENTO") &&
                    !m.startsWith("CONTEXTO") &&
                    !m.startsWith("FIN") &&
                    !m.startsWith("ADVERTENCIA") &&
                    !m.startsWith("FUENTE") &&
                    !m.startsWith("http://") &&
                    !m.startsWith("https://")
            );
        const unique = Array.from(new Set(cleaned));
        setPlaceholders(unique);
        return unique;
    }, [editor]);

    // Auto-scan placeholders when content is injected and open modal once if there are any
    useEffect(() => {
        if (contentInjected && editor && !hasAutoOpenedModalRef.current) {
            const found = scanPlaceholders();
            if (found.length > 0) {
                hasAutoOpenedModalRef.current = true;
                setIsFillerModalOpen(true);
            }
        }
    }, [contentInjected, editor, scanPlaceholders]);

    const handleEditSingleVariable = (varName: string) => {
        setEditingVariable(varName);
    };

    const handleSaveSingleVariable = (newVal: string) => {
        if (!editingVariable || !editor) return;
        const cleanedVar = editingVariable.replace(/^\[|\]$/g, '').trim();
        let html = editor.getHTML();
        const escaped = cleanedVar.replace(/[-\/\^$*+?.()|[\]{}]/g, '\$&');
        const spanPattern = new RegExp(`<span[^>]*class="[^"]*variable-chip-mark[^"]*"[^>]*data-variable="${escaped}"[^>]*>[\s\S]*?<\/span>`, 'gi');
        const bracketPattern = new RegExp(`\\[${escaped}\\]`, 'gi');
        let updated = html.replace(spanPattern, `<strong>${newVal.trim()}</strong>`);
        updated = updated.replace(bracketPattern, `<strong>${newVal.trim()}</strong>`);
        editor.commands.setContent(updated, { emitUpdate: true });
        localStorage.setItem(`learned_placeholder_${cleanedVar.toUpperCase()}`, newVal.trim());
        scanPlaceholders();
        toast.success(`Variable [${cleanedVar}] actualizada a "${newVal.trim()}"`);
        setEditingVariable(null);
    };

    const handleApplyPlaceholders = (replacements: Record<string, string>) => {
        if (!editor) return;
        let html = editor.getHTML();
        let count = 0;
        for (const [key, val] of Object.entries(replacements)) {
            if (val && val.trim()) {
                const escapedKey = key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                const spanPattern = new RegExp(`<span[^>]*class="[^"]*variable-chip-mark[^"]*"[^>]*data-variable="${escapedKey}"[^>]*>[\\s\\S]*?<\\/span>`, 'gi');
                const targetPattern = new RegExp(`\\\\[${escapedKey}\\\\]`, 'gi');
                html = html.replace(spanPattern, `<strong>${val.trim()}</strong>`);
                html = html.replace(targetPattern, `<strong>${val.trim()}</strong>`);
                localStorage.setItem(`learned_placeholder_${key.toUpperCase().trim()}`, val.trim());
                count++;
            }
        }
        editor.commands.setContent(html, { emitUpdate: true });
        scanPlaceholders();
        toast.success(`${count} campos completados y actualizados en el editor`);
    };

    const loadProcesos = async () => {
        setLoadingProcesos(true);
        try {
            const list = await scraperApi.fetchScrapperProcesos(userId);
            setProcesos(list || []);
        } catch (error) {
            console.error("Error loading processes in LegalEditor:", error);
            toast.error("Error al cargar los expedientes de procesos.");
        } finally {
            setLoadingProcesos(false);
        }
    };

    const handleLinkToProceso = async () => {
        if (!selectedProcesoRadicado) {
            toast.error("Por favor, selecciona un expediente.");
            return;
        }

        const cleanRadicado = selectedProcesoRadicado.replace(/\s/g, "");
        const name = (linkProcesoFileName.trim() || "Documento_Vinculado").replace(/\s+/g, "_");
        const finalFileName = name.endsWith(`.${linkProcesoFormat}`) ? name : `${name}.${linkProcesoFormat}`;

        setIsLinkingProceso(true);
        try {
            const html = editor?.getHTML() || "";
            if (!html || html.trim() === "<p></p>") {
                toast.error("El documento está vacío.");
                return;
            }

            const token = localStorage.getItem("auth.access_token") || "";
            const scrapperUrl = import.meta.env.VITE_SCRAPPER_API_URL || "http://localhost:8082";
            const exportRes = await fetch(`${scrapperUrl}/api/procesos/${cleanRadicado}/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "ngrok-skip-browser-warning": "true",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    content: html,
                    format: linkProcesoFormat,
                    title: name
                })
            });

            if (!exportRes.ok) {
                const errText = await exportRes.text().catch(() => "Unknown error");
                throw new Error(`Error en la generación del documento (${exportRes.status}): ${errText}`);
            }

            const blob = await exportRes.blob();

            const mimeType = linkProcesoFormat === "pdf"
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            const fileToUpload = new File([blob], finalFileName, { type: mimeType });

            await scraperApi.uploadProcesoFile(cleanRadicado, fileToUpload);

            toast.success(`Documento vinculado con éxito al expediente ${selectedProcesoRadicado}.`);
            window.dispatchEvent(new Event("storage-updated"));
            setShowLinkProcesoModal(false);
        } catch (error: any) {
            console.error("Error linking to process:", error);
            toast.error(error.message || "Error al vincular el documento al proceso.");
        } finally {
            setIsLinkingProceso(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!editor) return;

        toast.loading("Generando PDF de alta calidad...");

        try {
            const doc = new jsPDF({
                orientation: "p",
                unit: "mm",
                format: "a4"
            });

            // Target the editor content element
            const element = document.querySelector(".tiptap-editor") as HTMLElement;
            if (!element) throw new Error("No se encontró el contenido del editor.");

            // Use doc.html for robust rendering including styles and images
            await doc.html(element, {
                callback: function (doc) {
                    const date = new Date().toISOString().split('T')[0];
                    doc.save(`Documento_Legal_${date}.pdf`);
                    toast.dismiss();
                    toast.success("PDF generado con éxito.");
                },
                x: 10,
                y: 10,
                width: 190, // A4 width is 210mm, minus margins
                windowWidth: 800, // Fixed width for consistent layout
                autoPaging: 'slice', // Important for long documents
            });
        } catch (err) {
            console.error("PDF generation error:", err);
            toast.dismiss();
            toast.error("Error al generar PDF. Usando método de respaldo...");

            // Fallback to basic text method if HTML rendering fails
            const doc = new jsPDF();
            const content = editor.getText();
            const splitText = doc.splitTextToSize(content, 180);
            doc.text(splitText, 15, 15);
            const date = new Date().toISOString().split('T')[0];
            doc.save(`Documento_Legal_Respaldo_${date}.pdf`);
        }
    };

    const processHtmlForDocx = async (html: string): Promise<string> => {
        if (!html || html.trim() === "<p></p>") return html;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const imgs = doc.querySelectorAll("img");
        
        for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i];
            
            // Get width from attribute or style
            let widthStr = img.getAttribute("width") || img.style.width || "";
            let widthVal = parseInt(widthStr.replace(/[^0-9]/g, ""), 10);
            
            if (!widthVal || isNaN(widthVal)) {
                widthVal = 200; // fallback width
            }

            const src = img.getAttribute("src") || "";
            let heightVal = 100; // default/fallback height
            
            if (src) {
                try {
                    const dims = await new Promise<{width: number, height: number}>((resolve) => {
                        const tempImg = new Image();
                        tempImg.onload = () => resolve({ width: tempImg.naturalWidth, height: tempImg.naturalHeight });
                        tempImg.onerror = () => resolve({ width: 0, height: 0 });
                        tempImg.src = src;
                    });
                    if (dims.width > 0 && dims.height > 0) {
                        const ratio = dims.width / dims.height;
                        heightVal = Math.round(widthVal / ratio);
                    } else {
                        heightVal = Math.round(widthVal / 2);
                    }
                } catch (e) {
                    heightVal = Math.round(widthVal / 2);
                }
            } else {
                heightVal = Math.round(widthVal / 2);
            }

            // Explicitly set unitless width and height attributes (which html-docx expects)
            img.setAttribute("width", String(widthVal));
            img.setAttribute("height", String(heightVal));
            
            // Set inline style for CSS-aware renderers
            img.setAttribute("style", `width: ${widthVal}px; height: ${heightVal}px; max-width: 100%; display: block; margin: 10px 0;`);
        }
        
        return doc.body.innerHTML;
    };

    const handleDownloadDOCX = async () => {
        if (!editor) return;
        try {
            const html = editor.getHTML();
            const processedHtml = await processHtmlForDocx(html);
            const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${processedHtml}</body></html>`;
            const blob = await asBlob(fullHtml);
            const date = new Date().toISOString().split('T')[0];
            const safeTitle = (currentTitle || "Documento_Legal").replace(/[\\/:*?"<>|]/g, "_").trim().replace(/\s+/g, "_");
            saveAs(blob as Blob, `${safeTitle}_${date}.docx`);
            toast.success("Word descargado con éxito.");
        } catch (err) {
            console.error("DOCX error:", err);
            toast.error("Error al generar Word.");
        }
    };

    const handleOpenLetterheadModal = async () => {
        setLoadingLetterheads(true);
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
            
            const res = await fetch(`${backendUrl}/api/v1/documents/templates`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!res.ok) throw new Error("Fallo al obtener plantillas");
            const data = await res.json();
            const templates = data.templates || [];
            
            if (templates.length === 0) {
                toast.info("No tienes plantillas membretadas. Súbelas en la configuración de Ajustes -> Plantillas de Word.");
            } else {
                setLetterheadTemplates(templates);
                setShowLetterheadModal(true);
            }
        } catch (err) {
            console.error(err);
            toast.error("Error al obtener plantillas membretadas.");
        } finally {
            setLoadingLetterheads(false);
        }
    };

    const handleDownloadWithLetterhead = async (templatePath: string) => {
        if (!editor) return;
        setRenderingLetterhead(true);
        try {
            const htmlContent = editor.getHTML();
            const token = localStorage.getItem("auth.access_token") || "";
            const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
            
            const res = await fetch(`${backendUrl}/api/v1/documents/render-template`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    template_path: templatePath,
                    html_content: htmlContent
                })
            });
            
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: "Error de renderizado" }));
                throw new Error(err.detail || "Error al aplicar el membrete");
            }
            
            const blob = await res.blob();
            const date = new Date().toISOString().split('T')[0];
            const safeTitle = (currentTitle || "Documento").replace(/[\\/:*?"<>|]/g, "_").trim().replace(/\s+/g, "_");
            
            saveAs(blob, `${safeTitle}_membretado_${date}.docx`);
            toast.success("Documento con membrete descargado con éxito.");
            setShowLetterheadModal(false);
        } catch (error: any) {
            console.error("Error downloading with letterhead:", error);
            toast.error(error.message || "Error al exportar con membrete.");
        } finally {
            setRenderingLetterhead(false);
        }
    };

    const setPanelOnly = (panel: "comments" | "approvals" | "signatures" | "ai" | "none") => {
        setShowComments(panel === "comments");
        setShowApprovals(panel === "approvals");
        setShowSignatures(panel === "signatures");
        setShowAiMode(panel === "ai");
    };

    const handleApplyRefinement = async () => {
        if (!editor || !aiInstructions.trim()) return;
        setIsRefining(true);
        try {
            const htmlContent = editor.getHTML();
            const token = localStorage.getItem("auth.access_token") || "";
            const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
            
            const res = await fetch(`${backendUrl}/api/v1/documents/refine`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    html_content: htmlContent,
                    instructions: aiInstructions
                })
            });
            
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: "Error de refinamiento" }));
                throw new Error(err.detail || "Fallo en el servicio de refinamiento");
            }
            
            const data = await res.json();
            if (data.refined_content) {
                editor.commands.setContent(data.refined_content, { emitUpdate: true });
                toast.success("Documento refinado con éxito por la IA.");
                setAiInstructions("");
                setPanelOnly("none");
            } else {
                toast.error("No se recibió contenido refinado de la IA.");
            }
        } catch (error: any) {
            console.error("Error refining document:", error);
            toast.error(error.message || "Error al aplicar cambios de la IA.");
        } finally {
            setIsRefining(false);
        }
    };

    const handleExportToCRM = async () => {
        setExporting(true);
        try {
            // 1. Fetch active integrations for the user
            const integrations = await (apiClient as any).get("/api/v1/integrations");
            const crmIntegration = integrations?.find((i: any) => i.type === "hubspot" || i.type === "salesforce");

            if (!crmIntegration) {
                toast.error("No se encontró una integración de CRM activa. Configúrala en 'Integraciones'.");
                return;
            }

            // 2. Export document
            const response = await (apiClient as any).post("/api/v1/crm/export-document", {
                document_id: documentId,
                integration_id: crmIntegration.id,
                // If we have a ticket/object id in metadata, we could pass it here
            });

            if (response.status === "success") {
                toast.success(`Documento sincronizado con ${crmIntegration.type.toUpperCase()} correctamente.`);
            } else {
                throw new Error(response.message || "Error desconocido");
            }
        } catch (err: any) {
            console.error("[CRM Export Error]", err);
            toast.error("Fallo al exportar al CRM: " + (err.message || "Error de red"));
        } finally {
            setExporting(false);
        }
    };


    const handleOpenShareModal = () => {
        setShowShareModal(true);
        loadActiveShares();
        loadTeamCollaborators();
    };

    const loadActiveShares = async () => {
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/share-links`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setActiveShares(data.shares || []);
            }
        } catch (e) {
            console.error("Error loading share links", e);
        }
    };

    const handleGenerateClientLink = async () => {
        setLinkGenerating(true);
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/share-link`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    access_level: clientPerm,
                    client_name: clientNameInput.trim() || "Cliente",
                    expires_in_days: 30
                })
            });
            if (res.ok) {
                const data = await res.json();
                const fullUrl = `${window.location.origin}/d/${data.share_token}`;
                setGeneratedLink(fullUrl);
                await navigator.clipboard.writeText(fullUrl);
                toast.success("✅ Enlace generado y copiado al portapapeles");
                loadActiveShares();
            } else {
                toast.error("Error generando enlace");
            }
        } catch (err) {
            toast.error("Error al conectar con el servidor");
        } finally {
            setLinkGenerating(false);
        }
    };

    const handleRevokeShareLink = async (tokenToRevoke: string) => {
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/share-links/${tokenToRevoke}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success("Enlace revocado");
                loadActiveShares();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const loadTeamCollaborators = async () => {
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/collaborators`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTeamCollaborators(data.collaborators || []);
            }
        } catch (e) {
            console.error("Error loading collaborators", e);
        }
    };

    const handleSearchTeam = async (val: string) => {
        setTeamSearchQuery(val);
        const query = val.trim().replace(/^@/, '');
        if (!query) {
            setTeamSuggestions([]);
            return;
        }
        setIsSearchingTeam(true);
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/team/search?q=${encodeURIComponent(query)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTeamSuggestions(data.members || []);
            }
        } catch (e) {
            console.error("Error searching team:", e);
        } finally {
            setIsSearchingTeam(false);
        }
    };

    const handleSelectTeamMember = (member: any) => {
        setSelectedTeamMember(member);
        setTeamUserId(member.user_id);
        setTeamSearchQuery("");
        setTeamSuggestions([]);
    };

    const handleAddCollaborator = async () => {
        if (!teamUserId.trim()) return;
        setTeamLoading(true);
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/collaborators`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ user_id: teamUserId.trim(), role: teamRole })
            });
            if (res.ok) {
                toast.success("Colaborador agregado");
                setTeamUserId("");
                setSelectedTeamMember(null);
                setTeamSearchQuery("");
                loadTeamCollaborators();
            } else {
                toast.error("Error al agregar colaborador");
            }
        } finally {
            setTeamLoading(false);
        }
    };

    const handleRemoveCollaborator = async (collabUid: string) => {
        try {
            const token = localStorage.getItem("auth.access_token") || "";
            const res = await fetch(`${API}/api/v1/documents/${documentId}/collaborators/${collabUid}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success("Colaborador eliminado");
                loadTeamCollaborators();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleShareLink = async () => {
        const shareData = {
            title: 'Documento Legal - SarAI',
            text: 'Te comparto este documento legal generado por SarAI.',
            url: window.location.href,
        };

        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            try {
                await navigator.share(shareData);
                toast.success("Menú de compartir abierto.");
            } catch (err) {
                if ((err as Error).name !== 'AbortError') {
                    console.error("Error sharing:", err);
                    toast.error("Error al abrir el menú de compartir.");
                }
            }
        } else {
            // Fallback: Copy link to clipboard
            try {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Enlace copiado al portapapeles. ¡Ya puedes compartirlo!");
            } catch (err) {
                console.error("Clipboard error:", err);
                toast.error("No se pudo copiar el enlace al portapapeles.");
            }
        }
    };

    // Helper: generate PDF or DOCX blob from editor content
    const generateFileBlob = async (format: 'pdf' | 'docx'): Promise<{ blob: Blob; fileName: string }> => {
        if (!editor) throw new Error("Editor no disponible.");
        const date = new Date().toISOString().split('T')[0];
        const safeTitle = (currentTitle || "Documento_Legal").replace(/[\\/:*?"<>|]/g, "_").trim().replace(/\s+/g, "_");
        const fileName = `${safeTitle}_${date}.${format}`;

        if (format === 'pdf') {
            const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
            const element = document.querySelector(".tiptap-editor") as HTMLElement;
            if (!element) throw new Error("No se encontró el editor.");
            const blob = await new Promise<Blob>((resolve, reject) => {
                doc.html(element, {
                    callback: (d) => {
                        const b = d.output('blob');
                        b && b.size > 0 ? resolve(b) : reject(new Error("PDF vacío."));
                    },
                    x: 10, y: 10, width: 190, windowWidth: 800, autoPaging: 'slice'
                });
            });
            return { blob, fileName };
        } else {
            const html = editor.getHTML();
            if (!html || html.trim() === "<p></p>") throw new Error("Documento vacío.");
            const processedHtml = await processHtmlForDocx(html);
            const blob = await asBlob(`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${processedHtml}</body></html>`) as Blob;
            if (!blob || blob.size === 0) throw new Error("Word vacío.");
            return { blob, fileName };
        }
    };

    // Share via native OS dialog (WhatsApp, Bluetooth, email app, etc.)
    const handleShareNative = async (format: 'pdf' | 'docx') => {
        const loadingToast = toast.loading(`Generando ${format.toUpperCase()}…`);
        try {
            const { blob, fileName } = await generateFileBlob(format);
            const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

            // Always download first as safety net
            saveAs(blob, fileName);

            // Then try native share dialog
            const buffer = await blob.arrayBuffer();
            const file = new File([new Uint8Array(buffer)], fileName, { type: mimeType, lastModified: Date.now() });

            toast.dismiss(loadingToast);

            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({
                        files: [file],
                        title: fileName,
                        text: 'Documento legal generado por miasistentelegalia',
                    });
                    toast.success("Documento compartido.");
                } catch (e: any) {
                    if (e.name !== 'AbortError') {
                        toast.info(`📁 "${fileName}" descargado. Compártelo desde tu carpeta de descargas.`, { duration: 6000 });
                    }
                }
            } else {
                toast.success(`📁 "${fileName}" descargado. Compártelo por WhatsApp, correo o Bluetooth.`, { duration: 6000 });
            }
        } catch (err: any) {
            toast.dismiss(loadingToast);
            toast.error(err.message || "Error al generar el archivo.");
        }
    };

    // Open email modal for direct email delivery via Resend
    const handleShareFile = (format: 'pdf' | 'docx') => {
        setEmailModalFormat(format);
        setShareEmail("");
        setShareName("");
        setShareMessage("");
        setShowEmailModal(true);
    };

    const sendShareEmail = async () => {
        if (!editor || !shareEmail.includes("@")) {
            toast.error("Por favor, ingresa un correo válido.");
            return;
        }
        setShareSending(true);

        try {
            const { blob, fileName } = await generateFileBlob(emailModalFormat);

            // Convert to base64
            const buffer = await blob.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const base64Content = btoa(binary);

            const response = await fetch(`${API}/api/v1/documents/share-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    recipient_email: shareEmail,
                    recipient_name: shareName,
                    sender_name: userName,
                    file_name: fileName,
                    file_content_base64: base64Content,
                    file_type: emailModalFormat,
                    message: shareMessage,
                }),
            });

            if (response.ok) {
                toast.success(`✅ Documento enviado a ${shareEmail}`, { duration: 5000 });
                setShowEmailModal(false);
            } else {
                const err = await response.json().catch(() => ({ detail: "Error desconocido" }));
                throw new Error(err.detail || "Error al enviar.");
            }
        } catch (err: any) {
            console.error("Share email error:", err);
            toast.error(err.message || "Error al enviar el documento.");
        } finally {
            setShareSending(false);
        }
    };

    const saveLabel = saveStatus === "saving" ? "⏳ Guardando…"
        : saveStatus === "saved" ? "✅ Guardado"
            : saveStatus === "error" ? "❌ Error"
                : "💾 Guardar";

    const connLabel = !collabAvailable ? "📝 Modo local"
        : connected ? "● En línea" : "○ Conectando…";
    const connClass = !collabAvailable ? "offline"
        : connected ? "online" : "offline";

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="legal-editor-root">
            <style>{`
                .ProseMirror {
                    min-height: 500px;
                    padding: 2rem 3rem;
                    outline: none;
                    line-height: 1.8;
                    font-family: 'Inter', sans-serif;
                    color: #1e293b;
                }
                .ProseMirror p { margin-bottom: 1.5rem; }
                .ProseMirror h1 { font-size: 2rem; font-weight: 800; margin-bottom: 2rem; color: #0f172a; }
                .ProseMirror h2 { font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; color: #1e293b; }
                .ProseMirror h2 { font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; color: #1e293b; }
                
                /* Redline Styles - Force visibility in Editor */
                .ProseMirror ins, .ProseMirror .redline-ins {
                    color: #16a34a !important;
                    background-color: #dcfce7 !important;
                    text-decoration: none !important;
                    padding: 0 2px;
                    border-radius: 2px;
                    font-weight: 600;
                    border-bottom: 2px solid #22c55e;
                }
                .ProseMirror del, .ProseMirror .redline-del {
                    color: #dc2626 !important;
                    background-color: #fee2e2 !important;
                    text-decoration: line-through !important;
                    padding: 0 2px;
                    border-radius: 2px;
                    opacity: 0.8;
                }
                .ProseMirror ins.accepted {
                    background-color: transparent !important;
                    border-bottom: none !important;
                    color: #0f172a !important;
                    font-weight: normal;
                }
                
                /* Flashing Highlight for Citations */
                .citation-highlight {
                    background-color: #fde047 !important;
                    color: #000 !important;
                    animation: citationFlash 1.5s ease infinite;
                    border-radius: 2px;
                    padding: 0 2px;
                }
                
                @keyframes citationFlash {
                    0%, 100% { background-color: #fde047; }
                    50% { background-color: #fef9c3; transform: scale(1.02); }
                }

                .signature-image {
                    max-width: 100%;
                    height: auto;
                    border-bottom: 1px solid #ccc;
                    margin: 10px 0;
                    transition: outline-offset 0.2s, outline 0.2s, background 0.2s;
                    cursor: pointer;
                }
                .ProseMirror-selectednode.signature-image {
                    outline: 3px solid #6366f1;
                    outline-offset: 4px;
                    border-radius: 4px;
                    background: rgba(99, 102, 241, 0.05);
                }
                
                .editor-toolbar {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    padding: 0.75rem;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    position: sticky;
                    top: 0;
                    z-index: 20;
                }
                .toolbar-btn {
                    padding: 0.4rem 0.75rem;
                    border-radius: 0.5rem;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #475569;
                    transition: all 0.2s;
                    border: 1px solid transparent;
                }
                .toolbar-btn:hover { background: #e2e8f0; color: #0f172a; }
                .toolbar-btn.active { background: #0f172a; color: white; }
                .toolbar-separator { width: 1px; height: 1.5rem; background: #e2e8f0; margin: 0 0.25rem; }
            `}</style>

            {/* Header */}
            <div className="editor-header">
                <div className="header-left" style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0, flex: "1 1 auto" }}>
                    <span className="doc-title" style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontWeight: 700, minWidth: 0, maxWidth: isMobile ? "150px" : "none" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={currentTitle}>
                            ⚖️ {currentTitle}
                        </span>
                        <button 
                            type="button"
                            onClick={handleHeaderRename}
                            disabled={renaming}
                            style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "0.85rem", opacity: 0.7, flexShrink: 0 }}
                            title="Cambiar nombre del documento"
                        >
                            {renaming ? "⏳" : "✏️"}
                        </button>
                    </span>
                    <span className={`conn-badge ${connClass}`}>{connLabel}</span>
                </div>

                {!isMobile && (
                    <div className="presence-avatars">
                        {activeUsers.map(u => (
                            <span key={u.user_id} className="presence-avatar" style={{ background: u.color }} title={u.user_name}>
                                {u.user_name.charAt(0).toUpperCase()}
                            </span>
                        ))}
                        <span className="presence-avatar self" title={`${userName} (Tú)`}>{userName.charAt(0).toUpperCase()}</span>
                    </div>
                )}

                <div className="header-actions">
                    {isMobile ? (
                        <>
                            {onSave && (
                                <button type="button" className="btn-save" onClick={handleSave} disabled={saveStatus === "saving"} style={{ padding: "0.3rem 0.55rem" }}>
                                    {saveStatus === "saving" ? "⏳ Guardando" : "💾 Guardar"}
                                </button>
                            )}

                            {placeholders.length > 0 && (
                                <button
                                    type="button"
                                    className="btn-panel"
                                    onClick={() => {
                                        scanPlaceholders();
                                        setIsFillerModalOpen(true);
                                    }}
                                    style={{ background: "#f59e0b", color: "#0f172a", fontWeight: 700, padding: "0.3rem 0.55rem" }}
                                    title="Completar variables y campos entre corchetes"
                                >
                                    ✨ Variables ({placeholders.length})
                                </button>
                            )}

                            {!isPublicShare && (
                                <button
                                    type="button"
                                    className="btn-share"
                                    onClick={handleOpenShareModal}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #c7d2fe",
                                        background: "#eef2ff",
                                        color: "#4338ca",
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                        cursor: "pointer"
                                    }}
                                    title="Compartir documento con el cliente o equipo"
                                >
                                    <span>🔗 Compartir</span>
                                </button>
                            )}

                            <div className={`download-dropdown ${showDownloadMenu ? "open" : ""}`} ref={downloadRef}>
                                <button
                                    type="button"
                                    className="btn-download-trigger"
                                    title="Descargar"
                                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                    style={{ padding: "0.3rem 0.55rem" }}
                                >
                                    <FileDown size={15} />
                                    <span>Descargar</span>
                                    <ChevronDown size={12} className={showDownloadMenu ? "rotate-180" : ""} />
                                </button>
                                <div className="download-menu" style={{ right: 0, left: "auto" }}>
                                    <button onClick={() => { handleDownloadPDF(); setShowDownloadMenu(false); }}><FileText size={14} /> PDF (.pdf)</button>
                                    <button onClick={() => { handleDownloadDOCX(); setShowDownloadMenu(false); }}><Download size={14} /> Word (.docx)</button>
                                    <button onClick={() => { handleOpenLetterheadModal(); setShowDownloadMenu(false); }} disabled={loadingLetterheads}>
                                        {loadingLetterheads ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                        <span>Word con membrete</span>
                                    </button>
                                </div>
                            </div>

                            {/* Mobile More Options Dropdown */}
                            <div className={`mobile-more-dropdown ${showMobileMoreMenu ? "open" : ""}`} ref={mobileMoreMenuRef} style={{ position: "relative" }}>
                                <button
                                    type="button"
                                    className={`btn-panel ${showComments || showApprovals || showSignatures || showAiMode ? "active" : ""}`}
                                    onClick={() => setShowMobileMoreMenu(!showMobileMoreMenu)}
                                    title="Más opciones"
                                    style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontWeight: 600, padding: "0.3rem 0.55rem" }}
                                >
                                    <span>••• Más</span>
                                    {comments.filter(c => !c.resolved).length > 0 && (
                                        <span className="badge-count" style={{ position: "static", marginLeft: "2px" }}>
                                            {comments.filter(c => !c.resolved).length}
                                        </span>
                                    )}
                                </button>
                                {showMobileMoreMenu && (
                                    <div className="mobile-more-menu" style={{
                                        position: "absolute",
                                        top: "100%",
                                        right: 0,
                                        marginTop: "6px",
                                        background: "#ffffff",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "10px",
                                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.12)",
                                        zIndex: 1000,
                                        minWidth: "220px",
                                        padding: "6px 0",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "2px"
                                    }}>
                                        <button
                                            type="button"
                                            className="mobile-more-item"
                                            onClick={() => { setPanelOnly(showAiMode ? "none" : "ai"); setShowMobileMoreMenu(false); }}
                                            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", border: "none", background: showAiMode ? "#eff6ff" : "transparent", color: showAiMode ? "#2563eb" : "#1e293b", fontSize: "0.85rem", fontWeight: showAiMode ? 700 : 500, textAlign: "left", width: "100%", cursor: "pointer" }}
                                        >
                                            <span>🪄 Modo Asistente IA</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="mobile-more-item"
                                            onClick={() => { setPanelOnly(showComments ? "none" : "comments"); setShowMobileMoreMenu(false); }}
                                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", border: "none", background: showComments ? "#eff6ff" : "transparent", color: showComments ? "#2563eb" : "#1e293b", fontSize: "0.85rem", fontWeight: showComments ? 700 : 500, textAlign: "left", width: "100%", cursor: "pointer" }}
                                        >
                                            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>💬 Comentarios</span>
                                            {comments.filter(c => !c.resolved).length > 0 && (
                                                <span className="badge-count" style={{ position: "static" }}>{comments.filter(c => !c.resolved).length}</span>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="mobile-more-item"
                                            onClick={() => { setPanelOnly(showSignatures ? "none" : "signatures"); setShowMobileMoreMenu(false); }}
                                            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", border: "none", background: showSignatures ? "#eff6ff" : "transparent", color: showSignatures ? "#2563eb" : "#1e293b", fontSize: "0.85rem", fontWeight: showSignatures ? 700 : 500, textAlign: "left", width: "100%", cursor: "pointer" }}
                                        >
                                            <span>🔐 Firmas Digitales</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="mobile-more-item"
                                            onClick={() => { setPanelOnly(showApprovals ? "none" : "approvals"); setShowMobileMoreMenu(false); }}
                                            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", border: "none", background: showApprovals ? "#eff6ff" : "transparent", color: showApprovals ? "#2563eb" : "#1e293b", fontSize: "0.85rem", fontWeight: showApprovals ? 700 : 500, textAlign: "left", width: "100%", cursor: "pointer" }}
                                        >
                                            <span>✅ Aprobación de Versión</span>
                                        </button>
                                        <div style={{ height: "1px", background: "#f1f5f9", margin: "4px 0" }} />
                                        <button
                                            type="button"
                                            className="mobile-more-item"
                                            onClick={() => { setShowLinkProcesoModal(true); loadProcesos(); setShowMobileMoreMenu(false); }}
                                            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", border: "none", background: "transparent", color: "#1e293b", fontSize: "0.85rem", fontWeight: 500, textAlign: "left", width: "100%", cursor: "pointer" }}
                                        >
                                            <span>🔗 Vincular a Proceso</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="mobile-more-item"
                                            onClick={() => { navigate(`/dashboard/agents/document-history/${documentId}`); setShowMobileMoreMenu(false); }}
                                            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", border: "none", background: "transparent", color: "#1e293b", fontSize: "0.85rem", fontWeight: 500, textAlign: "left", width: "100%", cursor: "pointer" }}
                                        >
                                            <span>📋 Historial de Versiones</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <button type="button" className={`btn-panel ${showComments ? "active" : ""}`}
                                onClick={() => setPanelOnly(showComments ? "none" : "comments")}>
                                💬 Comentarios
                                {comments.filter(c => !c.resolved).length > 0 && (
                                    <span className="badge-count">{comments.filter(c => !c.resolved).length}</span>
                                )}
                            </button>
                            <button type="button" className={`btn-panel ${showApprovals ? "active" : ""}`}
                                onClick={() => setPanelOnly(showApprovals ? "none" : "approvals")}>
                                ✅ Aprobación
                            </button>
                            <button type="button" className={`btn-panel ${showSignatures ? "active" : ""}`}
                                onClick={() => setPanelOnly(showSignatures ? "none" : "signatures")}>
                                🔐 Firmas
                            </button>
                            <button type="button" className={`btn-panel ${showAiMode ? "active" : ""}`}
                                onClick={() => setPanelOnly(showAiMode ? "none" : "ai")}>
                                🪄 Modo IA
                            </button>
                            <button
                                type="button"
                                className="btn-panel"
                                onClick={() => {
                                    scanPlaceholders();
                                    setIsFillerModalOpen(true);
                                }}
                                style={placeholders.length > 0 ? { background: "#f59e0b", color: "#0f172a", fontWeight: 700 } : undefined}
                                title="Completar variables y campos entre corchetes"
                            >
                                ✨ Variables {placeholders.length > 0 ? `(${placeholders.length})` : ""}
                            </button>
                            <button type="button" className="btn-link-proceso"
                                onClick={() => {
                                    setShowLinkProcesoModal(true);
                                    loadProcesos();
                                }}>
                                🔗 Vincular
                            </button>
                            <button type="button" className="btn-history"
                                onClick={() => navigate(`/dashboard/agents/document-history/${documentId}`)}>
                                📋 Historial
                            </button>
                            {onSave && (
                                <button type="button" className="btn-save" onClick={handleSave} disabled={saveStatus === "saving"}>
                                    {saveLabel}
                                </button>
                            )}

                            {!isPublicShare && (
                                <button
                                    type="button"
                                    className="btn-share"
                                    onClick={handleOpenShareModal}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #c7d2fe",
                                        background: "#eef2ff",
                                        color: "#4338ca",
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                        cursor: "pointer"
                                    }}
                                    title="Compartir documento con el cliente o equipo"
                                >
                                    <span>🔗 Compartir</span>
                                </button>
                            )}

                            <div className={`download-dropdown ${showDownloadMenu ? "open" : ""}`} ref={downloadRef}>
                                <button
                                    type="button"
                                    className="btn-download-trigger"
                                    title="Descargar"
                                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                >
                                    <FileDown size={18} />
                                    <span>Descargar</span>
                                    <ChevronDown size={14} className={showDownloadMenu ? "rotate-180" : ""} />
                                </button>
                                <div className="download-menu">
                                    <button onClick={() => { handleDownloadPDF(); setShowDownloadMenu(false); }}><FileText size={14} /> PDF (.pdf)</button>
                                    <button onClick={() => { handleDownloadDOCX(); setShowDownloadMenu(false); }}><Download size={14} /> Word (.docx)</button>
                                    <button onClick={() => { handleOpenLetterheadModal(); setShowDownloadMenu(false); }} disabled={loadingLetterheads}>
                                        {loadingLetterheads ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                        <span>Word con membrete</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="editor-body">
                <div className="editor-main">
                    <Toolbar editor={editor} isLegalFormat={isLegalFormat} toggleLegalFormat={() => setIsLegalFormat(!isLegalFormat)} />
                    <div className={cn("editor-scroll", isLegalFormat && "legal-format-wrapper")}>
                        {editor && (
                            <BubbleMenu editor={editor}>
                                <div className="bubble-menu">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPanelOnly("comments");
                                            // Focus the comment input in the sidebar
                                            setTimeout(() => {
                                                const input = document.querySelector('.comment-input') as HTMLTextAreaElement;
                                                input?.focus();
                                            }, 100);
                                        }}
                                        className="bubble-menu-btn"
                                    >
                                        💬 Comentar
                                    </button>
                                    <span className="toolbar-separator" />
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleBold().run()}
                                        className={editor.isActive('bold') ? 'is-active' : ''}
                                    >
                                        <strong>B</strong>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleItalic().run()}
                                        className={editor.isActive('italic') ? 'is-active' : ''}
                                    >
                                        <em>I</em>
                                    </button>

                                    {editor.isActive('image') && (
                                        <>
                                            <span className="toolbar-separator" />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    editor.chain().focus().updateAttributes('image', { width: '100px' }).run();
                                                }}
                                                className={`bubble-menu-btn text-[11px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-50 mr-1 ${editor.getAttributes('image').width === '100px' ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-bold' : ''}`}
                                            >
                                                P (100px)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    editor.chain().focus().updateAttributes('image', { width: '150px' }).run();
                                                }}
                                                className={`bubble-menu-btn text-[11px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-50 mr-1 ${editor.getAttributes('image').width === '150px' ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-bold' : ''}`}
                                            >
                                                M (150px)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    editor.chain().focus().updateAttributes('image', { width: '200px' }).run();
                                                }}
                                                className={`bubble-menu-btn text-[11px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-50 mr-1 ${editor.getAttributes('image').width === '200px' ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-bold' : ''}`}
                                            >
                                                G (200px)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    editor.chain().focus().updateAttributes('image', { width: '300px' }).run();
                                                }}
                                                className={`bubble-menu-btn text-[11px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-50 mr-1 ${editor.getAttributes('image').width === '300px' ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-bold' : ''}`}
                                            >
                                                XG (300px)
                                            </button>
                                            <span className="toolbar-separator" />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const attrs = editor.getAttributes('image');
                                                    const currentWidth = parseInt(attrs.width || '200', 10);
                                                    const newWidth = Math.max(50, currentWidth - 20) + 'px';
                                                    editor.chain().focus().updateAttributes('image', { width: newWidth }).run();
                                                }}
                                                className="bubble-menu-btn px-1.5 hover:bg-slate-50 font-bold text-xs"
                                                title="Reducir tamaño"
                                            >
                                                ➖
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const attrs = editor.getAttributes('image');
                                                    const currentWidth = parseInt(attrs.width || '200', 10);
                                                    const newWidth = Math.min(800, currentWidth + 20) + 'px';
                                                    editor.chain().focus().updateAttributes('image', { width: newWidth }).run();
                                                }}
                                                className="bubble-menu-btn px-1.5 hover:bg-slate-50 font-bold text-xs"
                                                title="Aumentar tamaño"
                                            >
                                                ➕
                                            </button>
                                            <span className="toolbar-separator" />
                                            <button
                                                type="button"
                                                onClick={() => editor.chain().focus().deleteSelection().run()}
                                                className="bubble-menu-btn delete-btn"
                                                style={{ color: '#ef4444', fontWeight: 600 }}
                                            >
                                                🗑️ Eliminar Firma
                                            </button>
                                        </>
                                    )}
                                </div>
                            </BubbleMenu>
                        )}
                        {isLegalFormat ? (
                            <div className="tiptap-word-page-container">
                                {/* Letterhead Header Graphic - Exact slant layout replication */}
                                <div className="tiptap-word-page-header">
                                    <div className="tiptap-word-header-banner">
                                        <div className="tiptap-word-banner-left">
                                            <div className="tiptap-word-banner-gold-slant" />
                                            <div className="tiptap-word-banner-blue-shape">
                                                <div className="tiptap-word-logo-container">
                                                    <div className="tiptap-word-logo-icon">
                                                        <span className="tiptap-word-logo-n">N</span>
                                                        <span className="tiptap-word-logo-col">🏛️</span>
                                                        <span className="tiptap-word-logo-m">M</span>
                                                    </div>
                                                    <div className="tiptap-word-logo-name">NUDIER MENA</div>
                                                    <div className="tiptap-word-logo-title">ABOGADO</div>
                                                    <div className="tiptap-word-logo-tagline">DERECHO • ESTRATEGIA • RESULTADOS</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="tiptap-word-banner-right">
                                            <div className="tiptap-word-contact-item">
                                                <span className="tiptap-word-contact-icon">📍</span>
                                                <span>Calle 93 # 14-45, Oficina 501<br />Medellín, Colombia</span>
                                            </div>
                                            <div className="tiptap-word-contact-item">
                                                <span className="tiptap-word-contact-icon">📞</span>
                                                <span>+57 320 123 4567</span>
                                            </div>
                                            <div className="tiptap-word-contact-item">
                                                <span className="tiptap-word-contact-icon">✉️</span>
                                                <span>nudier.mena.abogado@gmail.com</span>
                                            </div>
                                            <div className="tiptap-word-contact-item">
                                                <span className="tiptap-word-contact-icon">🌐</span>
                                                <span>www.nudiermenaabogado.com</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Editor */}
                                <EditorContent editor={editor} className="tiptap-editor" />
                                
                                {/* Letterhead Footer Graphic */}
                                <div className="tiptap-word-page-footer">
                                    <div className="tiptap-word-footer-divider" />
                                    <div className="tiptap-word-footer-content">
                                        <div>Celular: 311 383 6059 | Email: nudier716@hotmail.com | Medellín - Colombia</div>
                                        <div className="tiptap-word-footer-page">Página 1 de 1</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <EditorContent editor={editor} className="tiptap-editor" />
                        )}
                    </div>
                    <div className="editor-footer">
                        <span className="char-count">{charCount.toLocaleString()} caracteres</span>
                        <span className="collab-user-count">
                            {activeUsers.length + 1} usuario{activeUsers.length !== 0 ? "s" : ""} conectado{activeUsers.length !== 0 ? "s" : ""}
                        </span>
                    </div>
                </div>

                {showComments && (
                    <div className="side-panel">
                        <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>💬 Comentarios</span>
                            <button
                                type="button"
                                onClick={() => setPanelOnly("none")}
                                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#64748b", padding: "2px 8px", borderRadius: "4px" }}
                                title="Cerrar panel"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="comment-input-row">
                            <textarea className="comment-input" rows={2}
                                placeholder="Escribe un comentario (selecciona texto para anclar)…"
                                value={newComment} onChange={e => setNewComment(e.target.value)} />
                            <button type="button" className="btn-add-comment" onClick={addComment}
                                disabled={commentLoading || !newComment.trim()}>
                                {commentLoading ? "…" : "Añadir"}
                            </button>
                        </div>
                        <div className="comment-list">
                            {comments.length === 0 ? (
                                <div className="empty-panel">Sin comentarios aún.</div>
                            ) : comments.map((c, i) => (
                                <div
                                    key={c.id ?? i}
                                    id={`comment-${c.id}`}
                                    onClick={() => c.id && findAndHighlightComment(c.id, c.anchor_text)}
                                    className={`comment-card ${c.resolved ? "resolved" : ""} ${activeCommentId === c.id ? "active" : ""}`}
                                >
                                    <div className="comment-meta">
                                        <strong>{c.user_name}</strong>
                                        <span>{new Date(c.created_at).toLocaleDateString("es-CO")}</span>
                                    </div>
                                    {c.anchor_text && <blockquote className="comment-anchor">"{c.anchor_text}"</blockquote>}
                                    <p className="comment-body">{c.body}</p>
                                    {!c.resolved && c.id && (
                                        <button type="button" className="btn-resolve" onClick={() => resolveComment(c.id!)}>
                                            ✓ Resolver
                                        </button>
                                    )}
                                    {c.resolved && <span className="resolved-badge">✓ Resuelto</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {showApprovals && (
                    <div className="side-panel">
                        <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>✅ Aprobación de versión</span>
                            <button
                                type="button"
                                onClick={() => setPanelOnly("none")}
                                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#64748b", padding: "2px 8px", borderRadius: "4px" }}
                                title="Cerrar panel"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="approval-list">
                            {approvals.length === 0 ? (
                                <div className="empty-panel">No hay aprobaciones registradas.</div>
                            ) : approvals.map((a, i) => (
                                <div key={i} className={`approval-card status-${a.status}`}>
                                    <span className="approver-name">{a.approver_name}</span>
                                    <span className={`status-chip ${a.status}`}>
                                        {a.status === "approved" ? "✓ Aprobado" : a.status === "rejected" ? "✗ Rechazado" : "⏳ Pendiente"}
                                    </span>
                                    {a.comment && <p className="approval-comment">{a.comment}</p>}
                                </div>
                            ))}
                        </div>
                        <div className="approval-actions">
                            <p className="approval-hint">Tu decisión como revisor:</p>
                            <div className="approval-btn-row">
                                <button type="button" className="btn-approve" onClick={() => submitApproval("approved")}>✓ Aprobar</button>
                                <button type="button" className="btn-reject" onClick={() => {
                                    const c = window.prompt("Motivo del rechazo:"); if (c !== null) submitApproval("rejected", c);
                                }}>✗ Rechazar</button>
                            </div>
                        </div>
                    </div>
                )}

                {showSignatures && (
                    <div className="side-panel">
                        <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>🔐 Firmas Digitales</span>
                            <button
                                type="button"
                                onClick={() => setPanelOnly("none")}
                                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#64748b", padding: "2px 8px", borderRadius: "4px" }}
                                title="Cerrar panel"
                            >
                                ✕
                            </button>
                        </div>
                        <SignaturePanel
                            documentId={documentId}
                            documentText={editor?.getText() || ""}
                            userId={userId}
                            userName={userName}
                            onInsertSignature={(base64) => {
                                if (!editor) {
                                    console.error("[LegalEditor] editor instance is null");
                                    return;
                                }
                                // Use insertContent as it's often more robust for base64 images
                                editor.chain()
                                    .focus()
                                    .insertContent({
                                        type: 'image',
                                        attrs: {
                                            src: base64,
                                            class: 'signature-image'
                                        }
                                    })
                                    .run();
                            }}
                        />
                    </div>
                )}

                {showAiMode && (
                    <div className="side-panel">
                        <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>🪄 Asistente de Redacción IA</span>
                            <button
                                type="button"
                                onClick={() => setPanelOnly("none")}
                                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#64748b", padding: "2px 8px", borderRadius: "4px" }}
                                title="Cerrar panel"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Solicita a la IA añadir nuevas cláusulas, cambiar redacciones, corregir ortografía, o reestructurar partes del documento.
                            </p>
                            <div className="flex flex-col space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">Instrucciones para la IA</label>
                                <textarea
                                    className="w-full min-h-[140px] p-3 text-xs border border-slate-200 rounded-xl outline-none focus:border-blue-500 resize-none font-medium text-slate-700 leading-relaxed"
                                    placeholder="Ej: 'Añade una cláusula penal de incumplimiento al final del contrato', 'Corrige la redacción del párrafo tercero para que sea más formal'..."
                                    value={aiInstructions}
                                    onChange={e => setAiInstructions(e.target.value)}
                                    disabled={isRefining}
                                />
                            </div>
                            <button
                                type="button"
                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold text-xs transition-all disabled:opacity-50 shadow-md shadow-blue-500/10 cursor-pointer"
                                onClick={handleApplyRefinement}
                                disabled={isRefining || !aiInstructions.trim()}
                            >
                                {isRefining ? (
                                    <>
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        <span>Procesando cambios...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>🪄 Redactar con IA</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>


            {/* ─── Letterhead Selection Modal ─── */}
            {showLetterheadModal && (
                <div className="email-modal-overlay" onClick={() => !renderingLetterhead && setShowLetterheadModal(false)}>
                    <div className="email-modal" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
                        <div className="email-modal-header">
                            <span className="flex items-center gap-1.5 font-bold text-gray-800">
                                <FileText className="h-5 w-5 text-blue-500" />
                                Selecciona una plantilla membretada
                            </span>
                            <button
                                type="button"
                                className="email-modal-close"
                                onClick={() => setShowLetterheadModal(false)}
                                disabled={renderingLetterhead}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="email-modal-body" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                                Selecciona el membrete corporativo (.docx) en el que deseas incrustar la redacción actual de este documento:
                            </p>
                            <div className="space-y-2.5">
                                {letterheadTemplates.map((template, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="w-full flex items-center justify-between p-3.5 border border-slate-100 rounded-xl bg-white hover:bg-slate-50 hover:border-blue-400 text-left transition-all cursor-pointer group"
                                        onClick={() => handleDownloadWithLetterhead(template.path)}
                                        disabled={renderingLetterhead}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100/50 transition-colors">
                                                <FileText className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-slate-800 group-hover:text-blue-900">{template.name}</p>
                                                <p className="text-[10px] text-slate-400 mt-0.5">
                                                    Cargada el {new Date(template.created_at).toLocaleDateString("es-CO")}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        {renderingLetterhead && (
                            <div className="flex items-center justify-center gap-2 py-3 border-t mt-3 text-xs font-bold text-blue-600">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Generando documento con membrete...
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Modern Share Modal (Google Docs Link + Team) ─── */}
            {showShareModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(15, 23, 42, 0.45)",
                        backdropFilter: "blur(4px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 99999,
                    }}
                    onClick={() => setShowShareModal(false)}
                >
                    <div
                        style={{
                            background: "#ffffff",
                            borderRadius: "16px",
                            width: "90%",
                            maxWidth: "540px",
                            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>Compartir documento</h3>
                                <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: "#64748b" }}>Controla quién puede ver y colaborar en esta minuta legal.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowShareModal(false)}
                                style={{ background: "transparent", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#94a3b8" }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Tabs */}
                        <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                            <button
                                type="button"
                                onClick={() => setShareModalTab("client")}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    border: "none",
                                    borderBottom: shareModalTab === "client" ? "2px solid #4f46e5" : "2px solid transparent",
                                    background: "transparent",
                                    fontWeight: shareModalTab === "client" ? 700 : 500,
                                    color: shareModalTab === "client" ? "#4f46e5" : "#64748b",
                                    fontSize: "0.88rem",
                                    cursor: "pointer",
                                }}
                            >
                                🌐 Enlace para el Cliente
                            </button>
                            <button
                                type="button"
                                onClick={() => setShareModalTab("team")}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    border: "none",
                                    borderBottom: shareModalTab === "team" ? "2px solid #4f46e5" : "2px solid transparent",
                                    background: "transparent",
                                    fontWeight: shareModalTab === "team" ? 700 : 500,
                                    color: shareModalTab === "team" ? "#4f46e5" : "#64748b",
                                    fontSize: "0.88rem",
                                    cursor: "pointer",
                                }}
                            >
                                👥 Equipo del Despacho
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div style={{ padding: "24px" }}>
                            {shareModalTab === "client" ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                    <div>
                                        <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "#334155", marginBottom: "6px" }}>
                                            Nombre del Cliente (Opcional):
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="ej. Juan Pérez o Inversiones ABC"
                                            value={clientNameInput}
                                            onChange={e => setClientNameInput(e.target.value)}
                                            style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "0.9rem" }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "#334155", marginBottom: "6px" }}>
                                            Permisos del enlace:
                                        </label>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                                            {[
                                                { id: "edit", label: "✍️ Puede editar", desc: "Edición y notas en vivo" },
                                                { id: "comment", label: "💬 Solo comentar", desc: "Sugerencias y notas" },
                                                { id: "view", label: "👁️ Solo lectura", desc: "Ver y descargar PDF" },
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setClientPerm(opt.id as any)}
                                                    style={{
                                                        padding: "10px",
                                                        borderRadius: "8px",
                                                        border: clientPerm === opt.id ? "2px solid #4f46e5" : "1px solid #e2e8f0",
                                                        background: clientPerm === opt.id ? "#eef2ff" : "#ffffff",
                                                        textAlign: "left",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <div style={{ fontSize: "0.85rem", fontWeight: 600, color: clientPerm === opt.id ? "#4f46e5" : "#1e293b" }}>{opt.label}</div>
                                                    <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: "2px" }}>{opt.desc}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleGenerateClientLink}
                                        disabled={linkGenerating}
                                        style={{
                                            padding: "10px 16px",
                                            borderRadius: "8px",
                                            border: "none",
                                            background: "#4f46e5",
                                            color: "#ffffff",
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        {linkGenerating ? "Generando…" : "⚡ Crear Enlace Inmediato"}
                                    </button>

                                    {generatedLink && (
                                        <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                            <div style={{ fontSize: "0.8rem", color: "#166534", fontWeight: 600, marginBottom: "4px" }}>✓ Enlace activo copiado al portapapeles:</div>
                                            <input
                                                type="text"
                                                readOnly
                                                value={generatedLink}
                                                style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "0.82rem", background: "#ffffff", color: "#334155" }}
                                            />
                                        </div>
                                    )}

                                    {activeShares.length > 0 && (
                                        <div style={{ marginTop: "8px" }}>
                                            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>Enlaces activos:</div>
                                            <div style={{ maxHeight: "120px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
                                                {activeShares.map(s => (
                                                    <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f8fafc", borderRadius: "6px", fontSize: "0.8rem" }}>
                                                        <div>
                                                            <strong>{s.client_name || "Cliente"}</strong> ({s.access_level === "edit" ? "Edición" : s.access_level === "comment" ? "Comentarios" : "Lectura"})
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRevokeShareLink(s.share_token)}
                                                            style={{ border: "none", background: "transparent", color: "#ef4444", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem" }}
                                                        >
                                                            Revocar
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                    <div style={{ position: "relative" }}>
                                        <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "#334155", marginBottom: "6px" }}>
                                            Buscar colega en el despacho:
                                        </label>
                                        
                                        {selectedTeamMember ? (
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: "8px", background: "#eef2ff", border: "1px solid #c7d2fe" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#4f46e5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.78rem", fontWeight: 700 }}>
                                                        {(selectedTeamMember.full_name || "U").slice(0, 2).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#1e293b" }}>{selectedTeamMember.full_name}</div>
                                                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{selectedTeamMember.email}</div>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => { setSelectedTeamMember(null); setTeamUserId(""); }}
                                                    style={{ border: "none", background: "transparent", cursor: "pointer", color: "#64748b", fontSize: "1rem" }}
                                                    title="Cambiar usuario"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ position: "relative" }}>
                                                <input
                                                    type="text"
                                                    placeholder="Escribe @ o el nombre del colega..."
                                                    value={teamSearchQuery}
                                                    onChange={e => handleSearchTeam(e.target.value)}
                                                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "0.9rem" }}
                                                />
                                                {isSearchingTeam && (
                                                    <span style={{ position: "absolute", right: "12px", top: "10px", fontSize: "0.75rem", color: "#94a3b8" }}>Buscando…</span>
                                                )}

                                                {/* Mention Suggestions Floating Dropdown */}
                                                {teamSuggestions.length > 0 && (
                                                    <div style={{
                                                        position: "absolute",
                                                        top: "100%",
                                                        left: 0,
                                                        right: 0,
                                                        marginTop: "4px",
                                                        background: "#ffffff",
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: "8px",
                                                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                                                        zIndex: 10,
                                                        maxHeight: "180px",
                                                        overflowY: "auto"
                                                    }}>
                                                        {teamSuggestions.map(member => (
                                                            <div
                                                                key={member.user_id}
                                                                onClick={() => handleSelectTeamMember(member)}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "10px",
                                                                    padding: "10px 12px",
                                                                    cursor: "pointer",
                                                                    borderBottom: "1px solid #f1f5f9",
                                                                    transition: "background 0.15s",
                                                                }}
                                                                onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                                                                onMouseLeave={e => (e.currentTarget.style.background = "#ffffff")}
                                                            >
                                                                <div style={{
                                                                    width: "32px",
                                                                    height: "32px",
                                                                    borderRadius: "50%",
                                                                    background: "#e0e7ff",
                                                                    color: "#4338ca",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: "0.8rem",
                                                                    fontWeight: 700
                                                                }}>
                                                                    {(member.full_name || "C").slice(0, 2).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0f172a" }}>@{member.full_name}</div>
                                                                    <div style={{ fontSize: "0.74rem", color: "#64748b" }}>{member.email} • <span style={{ color: "#4f46e5" }}>{member.role}</span></div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <select
                                            value={teamRole}
                                            onChange={e => setTeamRole(e.target.value)}
                                            style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "0.9rem" }}
                                        >
                                            <option value="editor">Rol: Editor (puede modificar)</option>
                                            <option value="viewer">Rol: Lector (solo lectura)</option>
                                        </select>
                                        <button
                                            type="button"
                                            onClick={handleAddCollaborator}
                                            disabled={teamLoading || !teamUserId.trim()}
                                            style={{
                                                padding: "8px 18px",
                                                borderRadius: "8px",
                                                border: "none",
                                                background: teamUserId.trim() ? "#4f46e5" : "#94a3b8",
                                                color: "#ffffff",
                                                fontWeight: 600,
                                                cursor: teamUserId.trim() ? "pointer" : "not-allowed"
                                            }}
                                        >
                                            {teamLoading ? "Agregando…" : "Invitar al equipo"}
                                        </button>
                                    </div>

                                    <div>
                                        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>Miembros del equipo con acceso:</div>
                                        {teamCollaborators.length === 0 ? (
                                            <p style={{ fontSize: "0.82rem", color: "#94a3b8", fontStyle: "italic" }}>No hay colaboradores adicionales en esta minuta.</p>
                                        ) : (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "150px", overflowY: "auto" }}>
                                                {teamCollaborators.map(c => (
                                                    <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #f1f5f9", fontSize: "0.82rem" }}>
                                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                                            <span style={{ fontWeight: 600, color: "#1e293b" }}>{c.full_name || "Colega"}</span>
                                                            <span style={{ fontSize: "0.74rem", color: "#64748b" }}>{c.email || c.user_id} • <strong style={{ color: "#4338ca", textTransform: "capitalize" }}>{c.role}</strong></span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveCollaborator(c.user_id)}
                                                            style={{ border: "none", background: "transparent", color: "#ef4444", cursor: "pointer", fontWeight: 600, fontSize: "0.8rem" }}
                                                        >
                                                            Eliminar
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Email Share Modal ─── */}
            {showEmailModal && (
                <div className="email-modal-overlay" onClick={() => !shareSending && setShowEmailModal(false)}>
                    <div className="email-modal" onClick={e => e.stopPropagation()}>
                        <div className="email-modal-header">
                            <span>📧 Enviar {emailModalFormat.toUpperCase()} por correo</span>
                            <button type="button" className="email-modal-close" onClick={() => setShowEmailModal(false)} disabled={shareSending}>✕</button>
                        </div>
                        <div className="email-modal-body">
                            <label className="email-modal-label">Correo del destinatario *</label>
                            <input
                                type="email"
                                className="email-modal-input"
                                placeholder="ejemplo@correo.com"
                                value={shareEmail}
                                onChange={e => setShareEmail(e.target.value)}
                                autoFocus
                                disabled={shareSending}
                            />
                            <label className="email-modal-label">Nombre (opcional)</label>
                            <input
                                type="text"
                                className="email-modal-input"
                                placeholder="Nombre del destinatario"
                                value={shareName}
                                onChange={e => setShareName(e.target.value)}
                                disabled={shareSending}
                            />
                            <label className="email-modal-label">Mensaje (opcional)</label>
                            <textarea
                                className="email-modal-input email-modal-textarea"
                                placeholder="Agrega un mensaje personal..."
                                rows={3}
                                value={shareMessage}
                                onChange={e => setShareMessage(e.target.value)}
                                disabled={shareSending}
                            />
                        </div>
                        <div className="email-modal-footer">
                            <button type="button" className="email-modal-cancel" onClick={() => setShowEmailModal(false)} disabled={shareSending}>Cancelar</button>
                            <button
                                type="button"
                                className="email-modal-send"
                                onClick={sendShareEmail}
                                disabled={shareSending || !shareEmail.includes("@")}
                            >
                                {shareSending ? "Enviando…" : `Enviar ${emailModalFormat.toUpperCase()}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para Vincular a Expediente */}
            {showLinkProcesoModal && (
                <div className="email-modal-overlay" onClick={() => !isLinkingProceso && setShowLinkProcesoModal(false)}>
                    <div className="email-modal" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                        <div className="email-modal-header">
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 700 }}>
                                🔗 Vincular a Expediente
                            </span>
                            <button type="button" className="email-modal-close" onClick={() => setShowLinkProcesoModal(false)} disabled={isLinkingProceso}>✕</button>
                        </div>
                        <div className="email-modal-body" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                            <label className="email-modal-label">1. Seleccionar Expediente de Destino</label>
                            {loadingProcesos ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                                    <Loader2 className="animate-spin" style={{ width: '24px', height: '24px', color: '#6366f1' }} />
                                </div>
                            ) : procesos.length > 0 ? (
                                <select
                                    className="email-modal-input"
                                    value={selectedProcesoRadicado}
                                    onChange={(e) => {
                                        setSelectedProcesoRadicado(e.target.value);
                                        const p = procesos.find(proc => proc.numero_proceso === e.target.value);
                                        if (p) {
                                            setLinkProcesoFileName(p.title || "Documento_Vinculado");
                                        }
                                    }}
                                    disabled={isLinkingProceso}
                                    style={{ padding: '0.5rem', borderRadius: '6px', fontSize: '0.8rem', width: '100%', border: '1px solid #dadce0', margin: '0.25rem 0' }}
                                >
                                    <option value="">-- Selecciona un Proceso --</option>
                                    {procesos.map((p) => (
                                        <option key={p.numero_proceso} value={p.numero_proceso}>
                                            [{p.numero_proceso.substring(0, 10)}...] {p.title || "Sin título"} ({p.numero_proceso})
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <p style={{ fontSize: '0.75rem', color: '#ef4444', margin: '0.5rem 0' }}>No tienes expedientes de procesos activos.</p>
                            )}

                            {selectedProcesoRadicado && (
                                <>
                                    <label className="email-modal-label" style={{ marginTop: '1rem' }}>2. Formato del Archivo</label>
                                    <div style={{ display: 'flex', gap: '0.5rem', margin: '0.25rem 0' }}>
                                        <button
                                            type="button"
                                            onClick={() => setLinkProcesoFormat("docx")}
                                            style={{
                                                flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #dadce0',
                                                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                                                background: linkProcesoFormat === 'docx' ? '#e0e7ff' : '#fff',
                                                color: linkProcesoFormat === 'docx' ? '#4f46e5' : '#3c4043',
                                                borderColor: linkProcesoFormat === 'docx' ? '#6366f1' : '#dadce0'
                                            }}
                                        >
                                            Word (.docx)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setLinkProcesoFormat("pdf")}
                                            style={{
                                                flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #dadce0',
                                                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                                                background: linkProcesoFormat === 'pdf' ? '#fee2e2' : '#fff',
                                                color: linkProcesoFormat === 'pdf' ? '#dc2626' : '#3c4043',
                                                borderColor: linkProcesoFormat === 'pdf' ? '#fca5a5' : '#dadce0'
                                            }}
                                        >
                                            PDF (.pdf)
                                        </button>
                                    </div>

                                    <label className="email-modal-label" style={{ marginTop: '1rem' }}>3. Nombre del Archivo</label>
                                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #dadce0', borderRadius: '6px', padding: '0.25rem 0.5rem', background: '#f8fafc' }}>
                                        <input
                                            type="text"
                                            className="email-modal-input"
                                            value={linkProcesoFileName}
                                            onChange={(e) => setLinkProcesoFileName(e.target.value)}
                                            placeholder="Nombre del archivo (sin extensión)"
                                            disabled={isLinkingProceso}
                                            style={{ border: 'none', flex: 1, padding: '0.25rem 0', background: 'transparent', boxShadow: 'none', width: '100%' }}
                                        />
                                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>.{linkProcesoFormat}</span>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="email-modal-footer">
                            <button type="button" className="email-modal-cancel" onClick={() => setShowLinkProcesoModal(false)} disabled={isLinkingProceso}>Cancelar</button>
                            <button
                                type="button"
                                className="email-modal-send"
                                onClick={handleLinkToProceso}
                                disabled={isLinkingProceso || !selectedProcesoRadicado}
                                style={{ background: '#4f46e5', border: 'none', color: '#fff', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                {isLinkingProceso ? "Vinculando…" : "Confirmar Vinculación"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Word / Google Docs Styles ─── */}
            <style>{`
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
.legal-editor-root { display:flex;flex-direction:column;height:100%;background:#f0f0f0;color:#202124;font-family:'Inter',system-ui,sans-serif; }
.editor-header { display:flex;align-items:center;gap:.6rem;padding:.5rem 1rem;background:#fff;border-bottom:1px solid #dadce0;flex-shrink:0;flex-wrap:wrap; }
.header-left   { display:flex;align-items:center;gap:.5rem;flex:1; }
.btn-back-sm   { background:transparent;border:none;color:#5f6368;padding:.25rem .5rem;border-radius:4px;cursor:pointer;font-size:1.1rem; }
.btn-back-sm:hover { background:#f1f3f4; }
.doc-title     { font-weight:600;font-size:.95rem;color:#202124; }
.conn-badge    { font-size:.65rem;padding:.15rem .5rem;border-radius:99px;font-weight:600; }
.conn-badge.online  { background:#e6f4ea;color:#137333; }
.conn-badge.offline { background:#fff3e0;color:#e65100; }
.presence-avatars { display:flex;gap:3px;align-items:center; }
.presence-avatar  { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:700;color:#fff;border:2px solid #fff;cursor:default; }
.presence-avatar.self { border-color:#1a73e8; }
.header-actions { display:flex;gap:.4rem;flex-wrap:wrap; }
.btn-panel,.btn-history,.btn-save,.btn-link-proceso { padding:.35rem .7rem;border-radius:4px;font-size:.78rem;font-weight:500;cursor:pointer;border:1px solid #dadce0;background:#fff;color:#3c4043;position:relative;transition:background .1s; }
.btn-panel:hover,.btn-history:hover,.btn-link-proceso:hover { background:#f1f3f4; }
.btn-panel.active  { background:#e8f0fe;color:#1a73e8;border-color:#1a73e8; }
.btn-save          { background:#1a73e8;color:#fff;border-color:#1a73e8;font-weight:600; }
.btn-save:hover    { background:#185abc; }
.btn-save:disabled { opacity:.6;cursor:wait; }
.btn-ai-review {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.35rem 1rem; border-radius: 6px; font-size: 0.78rem; font-weight: 700;
    background: #4f46e5; color: #fff; border: none; cursor: pointer; transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
}
.btn-ai-review:hover { background: #4338ca; transform: translateY(-1px); }
.btn-ai-review.analyzing { opacity: 0.7; cursor: wait; background: #6366f1; }

.badge-count { position:absolute;top:-4px;right:-4px;background:#ea4335;color:#fff;border-radius:99px;font-size:.55rem;padding:0 4px;min-width:14px;text-align:center; }
.editor-toolbar  { display:flex;flex-wrap:wrap;gap:2px;padding:.3rem .75rem;background:#f8f9fa;border-bottom:1px solid #dadce0;flex-shrink:0; }
.toolbar-btn     { background:transparent;border:1px solid transparent;color:#444746;padding:.25rem .5rem;border-radius:4px;cursor:pointer;font-size:.82rem;font-weight:500;transition:all .08s; }
.toolbar-btn:hover  { background:#e8eaed; }
.toolbar-btn.active { background:#d3e3fd;color:#1a73e8;border-color:#a8c7fa; }
.toolbar-separator  { width:1px;background:#dadce0;margin:0 6px;align-self:stretch; }
.editor-body { display:flex;flex:1;overflow:hidden; }
.editor-main { display:flex;flex-direction:column;flex:1;overflow:hidden; }
.editor-scroll { flex:1;overflow-y:auto;padding:2rem 0;background:#f0f0f0; }
.tiptap-editor {
    max-width: 1000px; min-height: 1056px; margin: 0 auto;
    background: #fff; padding: 48px 64px;
    box-shadow:0 1px 3px rgba(0,0,0,.12), 0 1px 2px rgba(0,0,0,.08);
    border-radius:2px;
}
.tiptap-editor .ProseMirror {
    outline:none; min-height:900px;
    font-family:'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size:16px; line-height:1.75; color:#1f2937;
    text-align:left;
}
.tiptap-editor .ProseMirror h1 {
    font-family:'Inter',system-ui,sans-serif;
    font-size:1.5rem; font-weight:600; margin:1.75rem 0 0.5rem;
    color:#111827; border-bottom:1px solid #e5e7eb; padding-bottom:0.5rem;
}
.tiptap-editor .ProseMirror h2 {
    font-family:'Inter',system-ui,sans-serif;
    font-size:1.25rem; font-weight:600; margin:1.5rem 0 0.5rem; color:#1f2937;
}
.tiptap-editor .ProseMirror h3 {
    font-family:'Inter',system-ui,sans-serif;
    font-size:1.1rem; font-weight:600; margin:1.25rem 0 0.25rem; color:#374151;
}
.tiptap-editor .ProseMirror p { margin:0 0 1.25rem; text-align:left; text-indent:0; color:#1f2937; }
.tiptap-editor .ProseMirror strong { font-weight:700; }
.tiptap-editor .ProseMirror em { font-style:italic; }
.tiptap-editor .ProseMirror blockquote { border-left:3px solid #4285f4; padding-left:12pt; color:#5f6368; font-style:italic; margin:12pt 0; }
.tiptap-editor .ProseMirror ul { list-style-type:disc; margin:4pt 0 12pt 24pt; padding-left:0; }
.tiptap-editor .ProseMirror ol { list-style-type:decimal; margin:4pt 0 12pt 24pt; padding-left:0; }
.tiptap-editor .ProseMirror li { margin:2pt 0; padding-left:4pt; }
.tiptap-editor .ProseMirror li p { margin:0 0 2pt; }
.tiptap-editor .ProseMirror hr { border:none; border-top:1px solid #dadce0; margin:16pt 0; }
.tiptap-editor .ProseMirror .signature-image { max-width: 100%; height: auto; display: block; margin: 10px 0; border-bottom: 1px solid #eee; transition: none; }
.tiptap-editor .ProseMirror del, 
.tiptap-editor .ProseMirror .redline-del,
.tiptap-editor .ProseMirror s,
.tiptap-editor .ProseMirror strike {
    text-decoration: line-through !important;
    text-decoration-color: #dc2626 !important;
    color: #dc2626 !important;
    background-color: #fee2e2 !important;
    padding: 0 2px !important;
    display: inline !important;
    border-radius: 2px;
}
.tiptap-editor .ProseMirror ins, 
.tiptap-editor .ProseMirror .redline-ins,
.tiptap-editor .ProseMirror u {
    text-decoration: none !important;
    color: #16a34a !important;
    background-color: #dcfce7 !important;
    padding: 0 2px !important;
    display: inline !important;
    border-radius: 2px;
    font-weight: 600;
}
.tiptap-editor .ProseMirror .redline-ins.accepted {
    background-color: rgba(34, 197, 94, 0.05) !important;
    border-bottom: 2px solid #bbf7d0 !important;
    color: #15803d !important;
    text-decoration: none !important;
}







.tiptap-editor .ProseMirror .variable-chip-mark {
    background-color: #fef3c7 !important;
    color: #78350f !important;
    border: 1px solid #fcd34d !important;
    border-radius: 9999px !important;
    padding: 2px 8px !important;
    margin: 0 2px !important;
    font-weight: 700 !important;
    font-size: 0.95em !important;
    cursor: pointer !important;
    display: inline-block !important;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
    transition: all 0.15s ease !important;
    user-select: all !important;
}

.tiptap-editor .ProseMirror .variable-chip-mark:hover {
    background-color: #fde68a !important;
    border-color: #f59e0b !important;
    transform: translateY(-1px);
}

.tiptap-editor .ProseMirror .variable-chip-mark::before {
    content: "✏️ ";
    font-size: 0.85em;
    opacity: 0.8;
}

.comment-highlight {
    background-color: rgba(255, 212, 0, 0.25) !important;
    border-bottom: 2px solid #ffd400 !important;
    cursor: pointer;
    transition: all 0.2s ease;
}
.comment-highlight:hover {
    background-color: rgba(255, 212, 0, 0.45) !important;
}
.comment-highlight.active {
    background-color: rgba(255, 212, 0, 0.6) !important;
}

.bubble-menu {
    display: flex;
    background-color: #0f172a;
    padding: 0.2rem;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    gap: 0.2rem;
}
.bubble-menu button {
    border: none;
    background: none;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.3rem 0.6rem;
    border-radius: 0.3rem;
    cursor: pointer;
    transition: background 0.2s;
}
.bubble-menu button:hover {
    background-color: rgba(255, 255, 255, 0.1);
}
.bubble-menu button.is-active {
    color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.1);
}
.bubble-menu-btn {
    display: flex;
    align-items: center;
    gap: 0.3rem;
}
.tiptap-editor .ProseMirror p.is-editor-empty:first-child::before { content:attr(data-placeholder); float:left; color:#bbb; pointer-events:none; height:0; }
.tiptap-editor .ProseMirror .collaboration-cursor__caret { border-left:2px solid; margin-left:-1px; }
.tiptap-editor .ProseMirror .collaboration-cursor__label { font-size:.6rem; padding:.1rem .35rem; border-radius:3px; font-weight:600; white-space:nowrap; }
.editor-footer { display:flex;justify-content:space-between;padding:.3rem 1rem;background:#fff;border-top:1px solid #dadce0;font-size:.7rem;color:#80868b;flex-shrink:0; }
.side-panel     { width:400px;border-left:1px solid #dadce0;background:#fff;display:flex;flex-direction:column;overflow:hidden;flex-shrink:0; }
.panel-header   { padding:.75rem 1rem;font-weight:600;font-size:.88rem;border-bottom:1px solid #e0e0e0;color:#202124;flex-shrink:0; }
.empty-panel    { text-align:center;padding:2rem 1rem;color:#80868b;font-size:.82rem; }
.comment-input-row { display:flex;flex-direction:column;gap:.5rem;padding:.6rem;border-bottom:1px solid #e0e0e0;flex-shrink:0; }
.comment-input { background:#f8f9fa;border:1px solid #dadce0;border-radius:6px;color:#202124;padding:.5rem;font-size:.8rem;resize:none;font-family:'Inter',sans-serif; }
.comment-input:focus { outline:none;border-color:#1a73e8;box-shadow:0 0 0 2px rgba(26,115,232,.15); }
.btn-add-comment { background:#1a73e8;color:#fff;border:none;border-radius:6px;padding:.35rem .7rem;font-size:.8rem;font-weight:600;cursor:pointer;align-self:flex-end; }
.btn-add-comment:hover:not(:disabled) { background:#185abc; }
.btn-add-comment:disabled { opacity:.4;cursor:not-allowed; }
.comment-list { overflow-y:auto;flex:1;padding:.5rem;display:flex;flex-direction:column;gap:.4rem; }
.comment-card { background:#f8f9fa;border:1px solid #e0e0e0;border-radius:8px;padding:.6rem; cursor:pointer; transition: all 0.2s; }
.comment-card:hover { border-color: #1a73e8; background: #fff; }
.comment-card.active { border-color: #1a73e8; background: #e8f0fe; box-shadow: 0 2px 8px rgba(26,115,232,0.1); }
.comment-card.resolved { opacity:.45; cursor: default; }
.comment-meta { display:flex;justify-content:space-between;margin-bottom:.15rem;font-size:.7rem;color:#80868b; }
.comment-anchor { font-size:.7rem;color:#1a73e8;border-left:2px solid #1a73e8;margin:.15rem 0;padding:0 .4rem;font-style:italic; }
.comment-body { font-size:.8rem;margin:.15rem 0;color:#202124; }
.btn-resolve { background:transparent;border:1px solid #dadce0;color:#137333;font-size:.68rem;padding:.15rem .45rem;border-radius:4px;cursor:pointer; }
.btn-resolve:hover { background:#e6f4ea; }
.resolved-badge { font-size:.68rem;color:#137333; }
.approval-list    { flex:1;overflow-y:auto;padding:.5rem;display:flex;flex-direction:column;gap:.4rem; }
.approval-card    { background:#f8f9fa;border:1px solid #e0e0e0;border-radius:8px;padding:.6rem;display:flex;align-items:center;gap:.5rem;flex-wrap:wrap; }
.approver-name    { font-size:.85rem;font-weight:600;flex:1;color:#202124; }
.status-chip      { font-size:.68rem;padding:.15rem .45rem;border-radius:4px;font-weight:700; }
.status-chip.approved { background:#e6f4ea;color:#137333; }
.status-chip.rejected { background:#fce8e6;color:#c5221f; }
.status-chip.pending  { background:#e8f0fe;color:#1a73e8; }
.approval-comment { font-size:.7rem;color:#5f6368;margin:.2rem 0 0;width:100%; }
.approval-actions { padding:.6rem;border-top:1px solid #e0e0e0;flex-shrink:0; }
.approval-hint    { font-size:.72rem;color:#80868b;margin:0 0 .4rem; }
.approval-btn-row { display:flex;gap:.4rem; }
.btn-approve { flex:1;background:#e6f4ea;color:#137333;border:1px solid #a8dab5;border-radius:6px;padding:.35rem;font-size:.78rem;font-weight:700;cursor:pointer; }
.btn-approve:hover { background:#ceead6; }
.btn-reject  { flex:1;background:#fce8e6;color:#c5221f;border:1px solid #f5c6cb;border-radius:6px;padding:.35rem;font-size:.78rem;font-weight:700;cursor:pointer; }
.btn-reject:hover  { background:#f8d7da; }

.email-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex;
    align-items: center; justify-content: center; z-index: 9999;
    backdrop-filter: blur(2px); animation: fadeIn 0.15s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.email-modal {
    background: #fff; border-radius: 12px; width: 440px; max-width: 95vw;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
    animation: slideUp 0.2s ease;
}
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.email-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1rem 1.25rem; border-bottom: 1px solid #e2e8f0;
    font-weight: 600; font-size: 0.95rem; color: #0f172a;
}
.email-modal-close {
    background: none; border: none; font-size: 1.1rem; color: #94a3b8;
    cursor: pointer; padding: 0.25rem; border-radius: 4px; line-height: 1;
}
.email-modal-close:hover { background: #f1f5f9; color: #475569; }
.email-modal-body { padding: 1.25rem; display: flex; flex-direction: column; gap: 0.4rem; }
.email-modal-label { font-size: 0.75rem; font-weight: 600; color: #64748b; margin-top: 0.25rem; }
.email-modal-input {
    width: 100%; padding: 0.6rem 0.75rem; border: 1px solid #e2e8f0;
    border-radius: 8px; font-size: 0.85rem; font-family: 'Inter', sans-serif;
    color: #0f172a; background: #f8fafc; transition: all 0.15s;
    box-sizing: border-box;
}
.email-modal-input:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); background: #fff; }
.email-modal-input:disabled { opacity: 0.5; }
.email-modal-textarea { resize: vertical; min-height: 60px; }
.email-modal-footer {
    display: flex; justify-content: flex-end; gap: 0.5rem;
    padding: 0.75rem 1.25rem; border-top: 1px solid #e2e8f0; background: #f8fafc;
}
.email-modal-cancel {
    padding: 0.5rem 1rem; border: 1px solid #e2e8f0; border-radius: 8px;
    background: #fff; color: #64748b; font-size: 0.82rem; font-weight: 500;
    cursor: pointer; transition: all 0.15s;
}
.email-modal-cancel:hover { background: #f1f5f9; color: #334155; }
.email-modal-send {
    padding: 0.5rem 1.25rem; border: none; border-radius: 8px;
    background: #6366f1; color: #fff; font-size: 0.82rem; font-weight: 700;
    cursor: pointer; transition: all 0.15s;
}
.email-modal-send:hover:not(:disabled) { background: #4f46e5; transform: translateY(-1px); }
.email-modal-send:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-crm-export, .btn-download-trigger, .btn-share-trigger {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.45rem 1rem; border-radius: 8px; font-size: 0.78rem; font-weight: 700;
    color: #fff; border: none; cursor: pointer; transition: all 0.2s;
    margin-left: 0.5rem; white-space: nowrap;
}
.btn-crm-export {
    background: #10b981;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}
.btn-crm-export:hover { background: #059669; transform: translateY(-1px); }
.btn-crm-export.loading { opacity: 0.7; cursor: wait; }
.btn-crm-export:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-download-trigger {
    background: #f8f9fa; color: #3c4043; border: 1px solid #dadce0;
}
.btn-download-trigger:hover { background: #f1f3f4; }

.btn-share-trigger {
    background: #6366f1; /* indigo-500 */
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}
.btn-share-trigger:hover { background: #4f46e5; transform: translateY(-1px); }

.download-dropdown, .share-dropdown { position: relative; display: flex; align-items: stretch; }
.download-dropdown.open .download-menu, .share-dropdown.open .share-menu { display: block; }
.rotate-180 { transform: rotate(180deg); }
.header-actions button svg { transition: transform 0.2s; }

.download-menu, .share-menu {
    display: none; position: absolute; top: 100%; right: 0; background: #fff;
    border: 1px solid #dadce0; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    z-index: 1000; min-width: 180px; margin-top: 4px; padding: 4px 0;
}
.download-menu button, .share-menu button {
    display: flex; align-items: center; gap: 0.6rem; width: 100%; padding: 0.6rem 1rem;
    border: none; background: none; color: #3c4043; font-size: 0.8rem; cursor: pointer;
    text-align: left;
}
.download-menu button:hover, .share-menu button:hover { background: #f8f9fa; color: #1a73e8; }
.share-menu-divider { height: 1px; background: #e2e8f0; margin: 4px 0; }

/* ─── FORMATO LEGAL AESTHETIC (Matched to document-drafting-v2) ─── */
.legal-format-wrapper .tiptap-editor {
    border: 1px solid #cbd5e1 !important;
    border-radius: 4px !important;
    padding: 3.5rem 4.5rem !important;
    box-shadow: 0 20px 50px rgba(0,0,0,0.1) !important;
    max-width: 1100px !important;
    width: 100% !important;
    margin: 0 auto !important;
    position: relative !important;
    overflow: hidden !important;
}
.legal-format-wrapper .tiptap-editor::before {
    content: '' !important;
    position: absolute !important;
    top: 0; left: 0; right: 0; height: 5px !important;
    background: #1e1b4b !important;
}

.legal-format-wrapper .tiptap-editor .ProseMirror {
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    color: #1f2937 !important;
    line-height: 1.75 !important;
    text-align: left !important;
    width: 100% !important;
    font-size: 16px !important;
    hyphens: auto;
}
.legal-format-wrapper .tiptap-editor .ProseMirror p {
    margin-bottom: 1.25rem !important;
    white-space: pre-wrap !important;
    text-indent: 0 !important;
    color: #1f2937 !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror h1, 
.legal-format-wrapper .tiptap-editor .ProseMirror h2, 
.legal-format-wrapper .tiptap-editor .ProseMirror h3 {
    font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
    color: #111827 !important;
    text-align: left !important;
    text-transform: none !important;
    letter-spacing: -0.01em !important;
    font-weight: 600 !important;
    margin-bottom: 1rem !important;
    margin-top: 1.75rem !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror h1 {
    font-size: 1.5rem !important; 
    border-bottom: 1px solid #e5e7eb !important; 
    padding-bottom: 0.5rem !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror h2 {
    font-size: 1.25rem !important; 
    text-align: left !important; 
    border-left: none !important; 
    padding-left: 0 !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror h3 {
    font-size: 1.1rem !important; 
    text-align: left !important; 
    text-decoration: none !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror ul, 
.legal-format-wrapper .tiptap-editor .ProseMirror ol {
    padding-left: 2.5rem !important;
    margin-bottom: 1.5rem !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror li {
    margin-bottom: 0.75rem !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror strong {
    font-weight: 900 !important;
    color: #000 !important;
}
.legal-format-wrapper .tiptap-editor .ProseMirror hr {
    border: 0 !important;
    height: 1px !important;
    background: #cbd5e1 !important;
    margin: 3rem 0 !important;
}


/* ─── PAGE BREAK STYLING ─── */
.tiptap-editor .ProseMirror .page-break {
    page-break-after: always;
    break-after: page;
    margin: 2.5rem 0;
    border-top: 2px dashed #94a3b8;
    position: relative;
    text-align: center;
}
.tiptap-editor .ProseMirror .page-break span {
    position: relative;
    top: -0.75rem;
    background: #ffffff;
    padding: 2px 12px;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    user-select: none;
}
@media print {
    .tiptap-editor .ProseMirror .page-break {
        border: none !important;
        margin: 0 !important;
        height: 0 !important;
        page-break-after: always !important;
        break-after: page !important;
    }
    .tiptap-editor .ProseMirror .page-break span {
        display: none !important;
    }
}

/* ─── MICROSOFT WORD LAYOUT VIEW ─── */
.tiptap-word-page-container {
    max-width: 950px;
    margin: 20px auto;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06) !important;
    border-radius: 4px;
    border: 1px solid #cbd5e1;
    position: relative;
    display: flex;
    flex-direction: column;
}

.tiptap-word-page-header {
    padding: 32px 48px 0px 48px;
    background: #ffffff;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    user-select: none;
}

/* HIGH FIDELITY SLANTED BANNER RECREATION */
.tiptap-word-header-banner {
    display: flex;
    justify-content: space-between;
    height: 145px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    border-radius: 4px;
}

.tiptap-word-banner-left {
    position: relative;
    width: 62%;
    height: 100%;
}

.tiptap-word-banner-blue-shape {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: #0b1a30;
    clip-path: polygon(0 0, 100% 0, 88% 100%, 0 100%);
    z-index: 2;
    display: flex;
    align-items: center;
    padding-left: 36px;
}

.tiptap-word-banner-gold-slant {
    position: absolute;
    top: 0; left: 0;
    width: 103%; height: 100%;
    background: #d4af37;
    clip-path: polygon(0 0, 100% 0, 88% 100%, 0 100%);
    z-index: 1;
}

.tiptap-word-logo-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: #ffffff;
    font-family: 'Times New Roman', Georgia, serif;
}

.tiptap-word-logo-icon {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
}

.tiptap-word-logo-n, .tiptap-word-logo-m {
    font-size: 26px;
    font-weight: bold;
    color: #ffffff;
    line-height: 1;
}

.tiptap-word-logo-col {
    font-size: 20px;
    color: #e2b842;
    line-height: 1;
    margin-top: -2px;
}

.tiptap-word-logo-name {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #ffffff;
    line-height: 1.1;
}

.tiptap-word-logo-title {
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.35em;
    color: #e2b842;
    margin-top: 1px;
    display: flex;
    align-items: center;
    width: 100%;
}

.tiptap-word-logo-title::before, .tiptap-word-logo-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2b842;
    margin: 0 6px;
}

.tiptap-word-logo-tagline {
    font-size: 6.5px;
    letter-spacing: 0.15em;
    color: #94a3b8;
    margin-top: 4px;
    text-transform: uppercase;
}

.tiptap-word-banner-right {
    width: 35%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    padding-right: 24px;
    text-align: left;
    font-size: 10px;
    color: #334155;
    font-family: 'Inter', sans-serif;
}

.tiptap-word-contact-item {
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1.3;
}

.tiptap-word-contact-icon {
    font-size: 10px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a73e8;
}

.tiptap-word-page-container .tiptap-editor {
    box-shadow: none !important;
    border: none !important;
    padding: 32px 48px 32px 48px !important;
    min-height: 800px !important;
    max-width: 100% !important;
}

/* RENDER THE FIRST TABLE AS THE PREMIUM GREY METADATA TABLE */
.legal-format-wrapper .tiptap-editor .ProseMirror table:first-of-type {
    width: 100% !important;
    background: #f1f5f9 !important;
    border: none !important;
    margin-bottom: 24px !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
    border-radius: 6px !important;
    overflow: hidden !important;
    border: 1px solid #cbd5e1 !important;
}

.legal-format-wrapper .tiptap-editor .ProseMirror table:first-of-type tr:hover {
    background: transparent !important;
}

.legal-format-wrapper .tiptap-editor .ProseMirror table:first-of-type td {
    padding: 8px 16px !important;
    border: none !important;
    font-family: 'Georgia', 'Times New Roman', serif !important;
    font-size: 10pt !important;
    line-height: 1.4 !important;
    color: #1e293b !important;
    text-align: left !important;
}

.legal-format-wrapper .tiptap-editor .ProseMirror table:first-of-type tr td:first-child {
    font-weight: bold !important;
    width: 110px !important;
    color: #000000 !important;
}

.tiptap-word-page-footer {
    padding: 12px 48px 32px 48px;
    background: #ffffff;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    user-select: none;
}

.tiptap-word-footer-divider {
    height: 1.5px;
    background: #e2e8f0;
    margin-bottom: 12px;
}

.tiptap-word-footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: 'Times New Roman', Times, Georgia, serif;
    font-size: 11px;
    color: #475569;
}

.tiptap-word-footer-page {
    font-weight: bold;
    color: #1e1b4b;
}

@media (max-width: 768px) {
    .editor-header {
        padding: 0.4rem 0.6rem !important;
        gap: 0.35rem !important;
        flex-wrap: nowrap !important;
        justify-content: space-between !important;
    }
    .header-left {
        min-width: 0 !important;
        flex: 1 1 auto !important;
        gap: 0.35rem !important;
    }
    .doc-title {
        font-size: 0.82rem !important;
        max-width: 140px !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
    }
    .conn-badge {
        font-size: 0.6rem !important;
        padding: 0.1rem 0.35rem !important;
        flex-shrink: 0 !important;
    }
    .header-actions {
        display: flex !important;
        align-items: center !important;
        gap: 0.25rem !important;
        flex-wrap: nowrap !important;
        flex-shrink: 0 !important;
    }
    .btn-panel, .btn-save, .btn-download-trigger {
        padding: 0.3rem 0.5rem !important;
        font-size: 0.72rem !important;
        white-space: nowrap !important;
    }

    /* Single-row swipeable toolbar */
    .editor-toolbar {
        flex-wrap: nowrap !important;
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch !important;
        scrollbar-width: none !important;
        padding: 0.3rem 0.5rem !important;
        gap: 3px !important;
    }
    .editor-toolbar::-webkit-scrollbar {
        display: none !important;
    }
    .toolbar-btn {
        flex-shrink: 0 !important;
        white-space: nowrap !important;
        padding: 0.2rem 0.4rem !important;
        font-size: 0.75rem !important;
    }

    /* Canvas padding fix: eliminates lateral squeeze and excessive bottom gap so text is fully readable */
    .editor-scroll {
        padding: 0.5rem 0.25rem 1rem !important;
    }
    .tiptap-editor {
        padding: 1.25rem 0.875rem !important;
        min-height: 480px !important;
        margin: 0 !important;
        max-width: 100% !important;
        border-radius: 6px !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
    }
    .tiptap-editor .ProseMirror {
        min-height: 400px !important;
        font-size: 14.5px !important;
        line-height: 1.65 !important;
        padding: 0 !important;
    }

    /* Formato legal override on mobile */
    .legal-format-wrapper .tiptap-editor {
        padding: 1.25rem 0.875rem !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
    }
    .legal-format-wrapper .tiptap-editor .ProseMirror {
        font-size: 14.5px !important;
        line-height: 1.65 !important;
    }
    .legal-format-wrapper .tiptap-editor .ProseMirror ul,
    .legal-format-wrapper .tiptap-editor .ProseMirror ol {
        padding-left: 1.25rem !important;
    }

    /* Full screen modal side-panels on mobile with clean backdrop */
    .side-panel {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 9999 !important;
        background: #fff !important;
        box-shadow: none !important;
        border-left: none !important;
    }

    /* Editor footer - rests cleanly above mobile bottom navigation */
    .editor-footer {
        padding: 0.35rem 0.75rem calc(3.5rem + env(safe-area-inset-bottom, 0px)) !important;
    }
}
            `}</style>
            <PlaceholderFillerModal
                placeholders={placeholders}
                isOpen={isFillerModalOpen}
                onClose={() => setIsFillerModalOpen(false)}
                onApply={handleApplyPlaceholders}
            />
            <EditVariableModal
                isOpen={Boolean(editingVariable)}
                variableName={editingVariable || ""}
                initialValue=""
                onClose={() => setEditingVariable(null)}
                onSave={handleSaveSingleVariable}
            />
        </div>
    );
};

export default LegalEditor;
